import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { MapPin, Calendar, Eye, Phone, Mail, MessageCircle, Share2, ChevronLeft, ChevronRight, User, Shield, Send, X, Loader2, ArrowLeftRight, Star, Flag, BadgeCheck, Gavel, TrendingUp, History, DollarSign, Users, Clock, Car, Bell, BellOff, Anchor, Sofa, Home, Key, Bed, Bath, Maximize, ParkingCircle, Package, ImagePlus, ShoppingBag, Check, UserCheck, Play, ZoomIn, Trophy, Navigation, ExternalLink, Briefcase, RotateCcw, TrendingDown, FileCheck } from 'lucide-react';
import axios from '../lib/axios';
import { toast } from '../utils/toastWithSound';
import { API, useAuth, useLanguage, useCurrency } from '../App';
import { getRentalPeriodLabel } from '../utils/currencyFormatter';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareModal from '../components/ShareModal';
import FavoriteButton from '../components/FavoriteButton';
import AuctionTimer from '../components/AuctionTimer';
import TrustScoreBadge from '../components/TrustScoreBadge';
import ListingMap from '../components/ListingMap';
import SellerPerformance from '../components/SellerPerformance';
import BidFeed from '../components/BidFeed';
import ListingTypeBadge from '../components/ListingTypeBadge';
import PriceDisplay from '../components/PriceDisplay';
import ListingPropertyGroups from '../components/ListingPropertyGroups';
import ProposeTradeModal from '../components/ProposeTradeModal';
import BoostListingModal from '../components/BoostListingModal';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import BottomSheet from '../components/ui/bottom-sheet';
import { formatCategorySlug } from '../utils/formatters';
import { triggerPushPrompt } from '../components/PushNotificationPrompt';
import { getSubcategoryTranslation, getCategoryTranslation } from '../i18n-subcategories';
import { LargePromoSection } from '../components/PromoBlock';
import PriceReductionModal from '../components/modals/PriceReductionModal';

// Helper function to normalize URLs - ensures they have a protocol
const normalizeUrl = (url) => {
  if (!url) return '';
  const trimmedUrl = url.trim();
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  // Add https:// if no protocol is present
  return `https://${trimmedUrl}`;
};

const ListingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const { formatPrice: formatCurrency, formatDualPrice, USD_TO_XCG, currency } = useCurrency();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false); // For full-screen image view
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [chatPhoto, setChatPhoto] = useState(null);
  const [chatPhotoPreview, setChatPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [chatImageLightbox, setChatImageLightbox] = useState(null); // For image lightbox
  const chatEndRef = useRef(null);
  const chatPhotoInputRef = useRef(null);
  const chatTextareaRef = useRef(null);
  const hasShownErrorRef = useRef(false); // Prevent double toast in StrictMode
  
  // Swipe gesture refs for mobile image navigation
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  
  // Auction-specific state
  const [bidAmount, setBidAmount] = useState('');
  const [placingBid, setPlacingBid] = useState(false);
  const [withdrawingBid, setWithdrawingBid] = useState(false);
  const [showBidHistory, setShowBidHistory] = useState(false);
  
  // Offer history state (for yard sales, bulk sales)
  const [offerHistory, setOfferHistory] = useState([]);
  const [offerStats, setOfferStats] = useState(null);
  const [showOfferHistory, setShowOfferHistory] = useState(false);
  const [loadingOffers, setLoadingOffers] = useState(false);
  
  // Make Offer state (for yard sales, bulk sales)
  const [offerAmount, setOfferAmount] = useState('');
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [showMakeOffer, setShowMakeOffer] = useState(false);
  const [withdrawingOffer, setWithdrawingOffer] = useState(null); // offer_id being withdrawn
  
  // Yard Sale Item Photo Modal
  const [yardSalePhotoModal, setYardSalePhotoModal] = useState(null); // {image, name, index}
  
  // Price Alert state
  const [hasAlert, setHasAlert] = useState(false);
  const [alertLoading, setAlertLoading] = useState(false);
  
  // Mobile Location Modal state
  const [showMobileLocationModal, setShowMobileLocationModal] = useState(false);
  
  // Mark as Sold state
  const [showMarkSoldModal, setShowMarkSoldModal] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [saleNotes, setSaleNotes] = useState('');
  const [markingSold, setMarkingSold] = useState(false);
  
  // Under Offer and Price Reduction state
  const [markingUnderOffer, setMarkingUnderOffer] = useState(false);
  const [showPriceReductionModal, setShowPriceReductionModal] = useState(false);
  const [reducingPrice, setReducingPrice] = useState(false);
  
  // Report Modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportingListing, setReportingListing] = useState(false);
  
  // Detail tabs state (no longer used - using ListingPropertyGroups component)
  const [activeTab, setActiveTab] = useState('overview');
  
  // Category properties for dynamic display
  const [categoryProperties, setCategoryProperties] = useState([]);
  
  // Trade proposals for listing owner
  const [tradeProposals, setTradeProposals] = useState([]);
  const [loadingTradeProposals, setLoadingTradeProposals] = useState(false);
  const [showTradeProposals, setShowTradeProposals] = useState(false);

  // Boost listing modal state (shown after publishing a new listing)
  const [showBoostModal, setShowBoostModal] = useState(false);
  
  // Check if user just published this listing
  useEffect(() => {
    if (location.state?.justPublished && listing) {
      // Only show boost modal for the listing owner
      if (user && listing.user_id === user.id) {
        // Small delay to let the page render first
        const timer = setTimeout(() => {
          setShowBoostModal(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [location.state?.justPublished, listing, user]);

  useEffect(() => {
    hasShownErrorRef.current = false; // Reset error state for new listing
    fetchListing();
  }, [id, user?.id]); // Re-fetch when user logs in/out to get user-specific data
  
  // Fetch category properties is now done in fetchListing
  
  // Check price alert is now done in fetchListing

  useEffect(() => {
    if (showChat && user && listing) {
      fetchMessages();
    }
  }, [showChat, user, listing]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-expand textarea when chat opens with pre-filled message
  useEffect(() => {
    if (showChat && chatTextareaRef.current && newMessage) {
      const textarea = chatTextareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
    }
  }, [showChat, newMessage]);

  // Body scroll lock when any modal is open (iOS compatible)
  useEffect(() => {
    const isAnyModalOpen = showChat || showVideoModal || showShareModal || showTradeModal || showMarkSoldModal || showReportModal || chatImageLightbox || showImageModal || yardSalePhotoModal || showPriceReductionModal;
    
    if (isAnyModalOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Apply scroll lock styles (iOS compatible)
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showChat, showVideoModal, showShareModal, showTradeModal, showMarkSoldModal, showReportModal, chatImageLightbox, showImageModal, yardSalePhotoModal, showPriceReductionModal]);

  // ESC key to close image modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showImageModal) setShowImageModal(false);
        if (yardSalePhotoModal) setYardSalePhotoModal(null);
      }
    };
    if (showImageModal || yardSalePhotoModal) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [showImageModal, yardSalePhotoModal]);
  
  // Handle openTrade URL param (for "Make another offer" flow)
  useEffect(() => {
    if (searchParams.get('openTrade') === 'true' && listing && user && listing.user_id !== user.id) {
      setShowTradeModal(true);
      // Clear the param from URL without navigating
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('openTrade');
      navigate(`/listing/${id}${newParams.toString() ? '?' + newParams.toString() : ''}`, { replace: true });
    }
  }, [searchParams, listing, user, id, navigate]);
  
  // Initial data fetches for offer history and trade proposals are done in fetchListing
  // These useEffects are kept for owner-specific re-fetches and will only run if data wasn't fetched initially
  
  // Fetch trade proposals for listing owner (re-fetch with full details)
  useEffect(() => {
    if (listing && user && listing.user_id === user.id && 
        (listing.listing_type === 'trade' || listing.trade?.open_for_trade)) {
      fetchTradeProposals();
    }
  }, [listing?.id, user?.id]);
  
  const fetchTradeProposals = async () => {
    if (!user || !listing) return;
    setLoadingTradeProposals(true);
    try {
      const response = await axios.get(`${API}/trade-proposals/listing/${listing.id}`);
      setTradeProposals(response.data.proposals || []);
    } catch (error) {
      console.error('Failed to fetch trade proposals:', error);
    } finally {
      setLoadingTradeProposals(false);
    }
  };
  
  // Live polling for bids/offers - refresh every 8 seconds
  useEffect(() => {
    if (!listing) return;
    
    const shouldPoll = listing.listing_type === 'auction' || 
                       listing.listing_type === 'yard_sale' || 
                       listing.listing_type === 'bulk';
    
    if (!shouldPoll) return;
    
    // Only poll when the offer/bid section is visible and expanded
    // For auctions, always poll (bidding is time-sensitive)
    // For yard sales, only poll when offer history is expanded to avoid UI flicker
    const pollInterval = setInterval(() => {
      if (listing.listing_type === 'auction') {
        // Refresh listing to get latest bids (auction is time-sensitive)
        fetchListing();
      } else if (showOfferHistory) {
        // Only refresh offer history when it's expanded (avoids UI flicker)
        fetchOfferHistory();
      }
    }, 15000); // Poll every 15 seconds (reduced frequency to avoid flicker)
    
    return () => clearInterval(pollInterval);
  }, [listing?.id, listing?.listing_type, showOfferHistory]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      const listingData = response.data;
      setListing(listingData);
      
      // Parallel fetch of secondary data after listing loads
      const parallelFetches = [];
      
      // Category properties
      if (listingData.category) {
        parallelFetches.push(
          axios.get(`${API}/categories/${listingData.category}/properties`, {
            params: {
              subcategory: listingData.subcategory,
              listing_type: listingData.listing_type
            }
          }).then(res => setCategoryProperties(res.data.properties || []))
            .catch(() => console.log('Could not fetch category properties'))
        );
      }
      
      // Price alert check (only if logged in)
      if (user) {
        parallelFetches.push(
          axios.get(`${API}/listings/${id}/has-alert`)
            .then(res => setHasAlert(res.data.has_alert))
            .catch(() => {})
        );
      }
      
      // Offer history for applicable listing types
      if (['auction', 'yard_sale', 'bulk'].includes(listingData.listing_type)) {
        parallelFetches.push(
          axios.get(`${API}/listings/${id}/offer-history`)
            .then(res => setOfferHistory(res.data.offers || []))
            .catch(() => {})
        );
      }
      
      // Trade proposals for trade listings
      if (listingData.listing_type === 'trade' && user) {
        parallelFetches.push(
          axios.get(`${API}/trade-proposals/listing/${listingData.id}`)
            .then(res => setTradeProposals(res.data || []))
            .catch(() => {})
        );
      }
      
      // Execute all parallel fetches
      await Promise.all(parallelFetches);
      
    } catch (error) {
      console.error('Failed to fetch listing:', error);
      const errorMsg = error.response?.data?.detail || 'Listing not found';
      // Only show toast once using a ref check (prevents StrictMode double-fire)
      if (!hasShownErrorRef.current) {
        hasShownErrorRef.current = true;
        toast.error(errorMsg);
      }
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if user has price alert
  const checkPriceAlert = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}/has-alert`);
      setHasAlert(response.data.has_alert);
    } catch (error) {
      console.error('Failed to check price alert:', error);
    }
  };
  
  // Toggle price alert
  const togglePriceAlert = async () => {
    if (!user) {
      toast.error('Please login to set price alerts');
      navigate('/auth', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }
    
    setAlertLoading(true);
    try {
      if (hasAlert) {
        // Get alert ID first
        const alertsResponse = await axios.get(`${API}/price-alerts`);
        const alert = alertsResponse.data.alerts.find(a => a.listing_id === id);
        if (alert) {
          await axios.delete(`${API}/price-alerts/${alert.id}`);
        }
        setHasAlert(false);
        toast.success('Price alert removed');
      } else {
        // Create alert
        const alertType = listing.listing_type === 'auction' ? 'outbid' : 'price_drop';
        await axios.post(`${API}/price-alerts`, {
          listing_id: id,
          alert_type: alertType,
        });
        setHasAlert(true);
        toast.success(alertType === 'outbid' ? 'You\'ll be notified when outbid!' : 'You\'ll be notified when price drops!');
      }
    } catch (error) {
      toast.error('Failed to update price alert');
    } finally {
      setAlertLoading(false);
    }
  };
  
  // Fetch offer history
  const fetchOfferHistory = async (showLoading = false) => {
    // Only show loading indicator on initial fetch, not on background polls
    const shouldShowLoading = showLoading || offerHistory.length === 0;
    if (shouldShowLoading) {
      setLoadingOffers(true);
    }
    try {
      const response = await axios.get(`${API}/listings/${id}/offer-history`);
      const newOffers = response.data.offers || [];
      const newStats = response.data.stats || null;
      
      // Only update state if data has actually changed (prevents unnecessary re-renders and flicker)
      const currentIds = offerHistory.map(o => o.id).join(',');
      const newIds = newOffers.map(o => o.id).join(',');
      if (currentIds !== newIds) {
        setOfferHistory(newOffers);
      }
      
      // Only update stats if changed
      const statsChanged = JSON.stringify(offerStats) !== JSON.stringify(newStats);
      if (statsChanged) {
        setOfferStats(newStats);
      }
    } catch (error) {
      console.error('Failed to fetch offer history:', error);
    } finally {
      // Only update loading state if we actually showed loading
      if (shouldShowLoading) {
        setLoadingOffers(false);
      }
    }
  };

  // Submit offer on yard sale / bulk sale
  const handleSubmitOffer = async () => {
    if (!user) {
      toast.error(language === 'nl' ? 'Log in om een bod te doen' : 'Please login to make an offer');
      navigate('/auth', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }

    const amount = parseFloat(offerAmount);
    if (!amount || amount <= 0) {
      toast.error(language === 'nl' ? 'Voer een geldig bedrag in' : 'Please enter a valid amount');
      return;
    }

    // Get the yard sale's native currency
    const ysCurrency = listing.yard_sale?.currency || listing.yard_sale?.items?.[0]?.currency || 'USD';
    
    // Convert the entered amount to the yard sale's native currency if needed
    let convertedAmount = amount;
    if (currency !== ysCurrency) {
      if (currency === 'XCG' && ysCurrency === 'USD') {
        // User entered XCG, convert to USD
        convertedAmount = amount / USD_TO_XCG;
      } else if (currency === 'USD' && ysCurrency === 'XCG') {
        // User entered USD, convert to XCG
        convertedAmount = amount * USD_TO_XCG;
      }
    }

    setSubmittingOffer(true);
    try {
      await axios.post(`${API}/listings/${id}/place-offer`, {
        amount: convertedAmount,
        currency: ysCurrency // Store in yard sale's native currency
      });

      toast.success(language === 'nl' ? 'Bod geplaatst!' : 'Offer submitted!');
      setOfferAmount('');
      setShowMakeOffer(false);
      fetchOfferHistory();
    } catch (error) {
      console.error('Failed to submit offer:', error);
      toast.error(error.response?.data?.detail || (language === 'nl' ? 'Kon bod niet plaatsen' : 'Failed to submit offer'));
    } finally {
      setSubmittingOffer(false);
    }
  };

  // Withdraw/cancel an offer
  const handleWithdrawOffer = async (offerId) => {
    if (!user) {
      toast.error(language === 'nl' ? 'Log in om je bod in te trekken' : 'Please login to withdraw your offer');
      return;
    }
    
    setWithdrawingOffer(offerId);
    try {
      // Let axios interceptor handle auth automatically (uses cookie or localStorage backup)
      await axios.delete(`${API}/offers/${offerId}`);
      toast.success(language === 'nl' ? 'Bod ingetrokken' : 'Offer withdrawn');
      fetchOfferHistory();
    } catch (error) {
      console.error('Failed to withdraw offer:', error);
      toast.error(error.response?.data?.detail || (language === 'nl' ? 'Kon bod niet intrekken' : 'Failed to withdraw offer'));
    } finally {
      setWithdrawingOffer(null);
    }
  };
  
  const fetchMessages = async () => {
    if (!user || !listing) return;
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/messages`);
      // Filter messages for this listing conversation
      const listingMessages = response.data.filter(
        m => m.listing_id === listing.id && 
        (m.sender_id === user.id || m.receiver_id === user.id)
      );
      setMessages(listingMessages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Place bid on auction
  const handlePlaceBid = async () => {
    if (!user) {
      toast.error('Please login to place a bid');
      navigate('/auth', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }

    const inputAmount = parseFloat(bidAmount);
    if (!inputAmount || isNaN(inputAmount)) {
      toast.error('Please enter a valid bid amount');
      return;
    }

    // Convert to USD if user is bidding in XCG
    // Backend always expects USD amounts
    const amountInUSD = currency === 'XCG' ? inputAmount / USD_TO_XCG : inputAmount;

    const auction = listing?.auction;
    const currentBid = auction?.current_bid || 0;
    const minBidUSD = Math.max(auction?.starting_price || 0, currentBid + (auction?.bid_increment || 1));

    if (amountInUSD < minBidUSD) {
      const minBidDisplay = currency === 'XCG' 
        ? `XCG ${(minBidUSD * USD_TO_XCG).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`
        : `$${minBidUSD.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`;
      toast.error(`Minimum bid is ${minBidDisplay}`);
      return;
    }

    setPlacingBid(true);
    try {
      await axios.post(`${API}/auctions/bid`, {
        listing_id: listing.id,
        amount: amountInUSD  // Always send USD to backend
      });
      
      const successDisplay = currency === 'XCG'
        ? `XCG ${inputAmount.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`
        : `$${amountInUSD.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`;
      toast.success(`Bid of ${successDisplay} placed successfully!`);
      setBidAmount('');
      // Trigger push notification prompt after placing bid
      triggerPushPrompt('bid');
      // Refresh listing to get updated bid info
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bid');
    } finally {
      setPlacingBid(false);
    }
  };

  const handleWithdrawBid = async () => {
    if (!user || !listing) return;
    
    // Confirm withdrawal
    const confirmed = window.confirm(
      'Are you sure you want to withdraw your bid? This action cannot be undone.'
    );
    if (!confirmed) return;
    
    setWithdrawingBid(true);
    try {
      const response = await axios.delete(`${API}/auctions/bid/${listing.id}`);
      
      const withdrawnDisplay = currency === 'XCG'
        ? `XCG ${(response.data.withdrawn_amount * USD_TO_XCG).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`
        : `$${response.data.withdrawn_amount.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`;
      
      toast.success(`Bid of ${withdrawnDisplay} withdrawn successfully`);
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw bid');
    } finally {
      setWithdrawingBid(false);
    }
  };

  const handleOpenChat = () => {
    if (!user) {
      toast.error('Please login to contact the seller');
      navigate('/auth', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }
    setShowChat(true);
    // Set default message if empty
    if (!newMessage) {
      setNewMessage(`Hi ${listing.user_name}, I'm interested in "${listing.title}". Is it still available?`);
    }
  };

  const handleSendMessage = async () => {
    if (!user) {
      toast.error('Please login to contact the seller');
      navigate('/auth');
      return;
    }

    if (!newMessage.trim() && !chatPhoto) {
      toast.error('Please enter a message or attach a photo');
      return;
    }

    setSending(true);
    try {
      let imageUrl = null;
      
      // Upload photo if attached
      if (chatPhoto) {
        setUploadingPhoto(true);
        const formData = new FormData();
        formData.append('file', chatPhoto);
        
        try {
          const uploadResponse = await axios.post(`${API}/upload`, formData, {
            headers: { 
              'Content-Type': 'multipart/form-data'
            }
          });
          imageUrl = uploadResponse.data.url;
        } catch (uploadError) {
          console.error('Photo upload failed:', uploadError);
          toast.error('Failed to upload photo');
          setSending(false);
          setUploadingPhoto(false);
          return;
        }
        setUploadingPhoto(false);
      }
      
      const messageContent = newMessage.trim() || (imageUrl ? '📷 Photo' : '');
      
      const response = await axios.post(
        `${API}/messages`,
        {
          listing_id: listing.id,
          receiver_id: listing.user_id,
          content: messageContent,
          message_type: 'inquiry',
          image_url: imageUrl,
        });
      
      // Add message to local state immediately
      const sentMessage = {
        id: response.data.id || Date.now().toString(),
        listing_id: listing.id,
        sender_id: user.id,
        receiver_id: listing.user_id,
        content: messageContent,
        image_url: imageUrl,
        created_at: new Date().toISOString(),
        sender_name: user.name,
      };
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');
      setChatPhoto(null);
      setChatPhotoPreview(null);
      toast.success('Message sent!');
      // Trigger push notification prompt after sending first message
      if (messages.length === 0) {
        triggerPushPrompt('message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Provide more specific error messages
      let errorMessage = language === 'nl' ? 'Bericht versturen mislukt' : 
                         language === 'es' ? 'Error al enviar mensaje' : 
                         'Failed to send message';
      
      if (error.response?.status === 403) {
        errorMessage = language === 'nl' ? 'Sessie verlopen, ververs de pagina' :
                       language === 'es' ? 'Sesión expirada, recarga la página' :
                       'Session expired, please refresh';
      } else if (error.response?.status === 401) {
        errorMessage = language === 'nl' ? 'Log opnieuw in om berichten te sturen' :
                       language === 'es' ? 'Inicia sesión para enviar mensajes' :
                       'Please log in again to send messages';
      } else if (!navigator.onLine) {
        errorMessage = language === 'nl' ? 'Geen internetverbinding' :
                       language === 'es' ? 'Sin conexión a internet' :
                       'No internet connection';
      }
      
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleChatPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setChatPhoto(file);
      setChatPhotoPreview(URL.createObjectURL(file));
    }
  };

  const removeChatPhoto = () => {
    setChatPhoto(null);
    setChatPhotoPreview(null);
    if (chatPhotoInputRef.current) {
      chatPhotoInputRef.current.value = '';
    }
  };

  // Handle changing yard sale item status
  const handleItemStatusChange = async (itemIndex, newStatus) => {
    if (!listing?.yard_sale?.items) return;
    
    try {
      // Create updated items array with the new status
      const updatedItems = listing.yard_sale.items.map((item, idx) => 
        idx === itemIndex ? { ...item, status: newStatus } : item
      );
      
      // Update the listing via API (using PUT endpoint)
      await axios.put(`${API}/listings/${listing.id}`, {
        yard_sale: {
          ...listing.yard_sale,
          items: updatedItems
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const statusMessages = {
        'sold': 'Item marked as sold',
        'reserved': 'Item reserved',
        'available': 'Item marked as available'
      };
      toast.success(statusMessages[newStatus] || 'Item updated');
      
      // Refresh the listing to get updated data
      fetchListing();
    } catch (error) {
      console.error('Failed to update item status:', error);
      toast.error('Failed to update item status');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Category-specific placeholder images
  const CATEGORY_IMAGES = {
    'real-estate': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&h=600&fit=crop',
    'vehicles': 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=600&fit=crop',
    'electronics': 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=800&h=600&fit=crop',
    'fashion': 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=800&h=600&fit=crop',
    'furniture': 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=600&fit=crop',
    'services': 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop',
    'jobs': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop',
    'other': 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800&h=600&fit=crop',
  };

  const getDefaultImage = (category) => {
    return CATEGORY_IMAGES[category] || CATEGORY_IMAGES['other'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="aspect-[16/9] bg-muted rounded-2xl mb-6"></div>
            <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-6 bg-muted rounded w-1/4 mb-6"></div>
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) return null;

  const defaultImage = getDefaultImage(listing.category);
  const images = listing.images?.length > 0 ? listing.images : [defaultImage];
  const isOwner = user && user.id === listing.user_id;

  // Swipe handlers for mobile image navigation
  const handleNextImage = () => {
    if (images.length > 1) {
      setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
  };
  
  const handlePrevImage = () => {
    if (images.length > 1) {
      setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }
  };

  // Touch/Swipe handlers for mobile image navigation
  const handleTouchStart = (e) => {
    if (!e?.targetTouches?.[0]) return;
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };
  
  const handleTouchMove = (e) => {
    if (!e?.targetTouches?.[0]) return;
    touchEndX.current = e.targetTouches[0].clientX;
  };
  
  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance
    
    if (distance > threshold) {
      // Swiped left - go to next image
      handleNextImage();
    } else if (distance < -threshold) {
      // Swiped right - go to previous image
      handlePrevImage();
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Open Mark as Sold modal
  const handleOpenMarkSoldModal = () => {
    setShowMarkSoldModal(true);
    // Pre-fill sale price with listing price
    if (listing.pricing?.amount) {
      setSalePrice(listing.pricing.amount.toString());
    }
  };

  // Handle mark as sold submission
  const handleMarkAsSold = async () => {
    setMarkingSold(true);
    try {
      const response = await axios.post(
        `${API}/listings/${listing.id}/mark-sold`,
        {
          sale_price: salePrice ? parseFloat(salePrice) : null,
          notes: saleNotes || null,
        });
      
      toast.success(response.data.message);
      setShowMarkSoldModal(false);
      
      // Refresh listing to show sold status
      fetchListing();
    } catch (error) {
      console.error('Failed to mark as sold:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as sold');
    } finally {
      setMarkingSold(false);
    }
  };

  // Handle unmark as sold (reactivate listing)
  const handleUnmarkSold = async () => {
    try {
      const response = await axios.post(
        `${API}/listings/${listing.id}/unmark-sold`);
      toast.success(response.data.message);
      fetchListing();
    } catch (error) {
      console.error('Failed to reactivate listing:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate listing');
    }
  };

  // Handle mark as reserved
  const handleMarkAsReserved = async () => {
    try {
      const response = await axios.post(
        `${API}/listings/${listing.id}/mark-reserved`);
      toast.success(response.data.message);
      fetchListing();
    } catch (error) {
      console.error('Failed to mark as reserved:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as reserved');
    }
  };

  // Handle unmark as reserved (reactivate listing)
  const handleUnmarkReserved = async () => {
    try {
      const response = await axios.post(
        `${API}/listings/${listing.id}/unmark-reserved`);
      toast.success(response.data.message);
      fetchListing();
    } catch (error) {
      console.error('Failed to remove reservation:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove reservation');
    }
  };

  // Handler for "Under Offer" status
  const handleMarkUnderOffer = async () => {
    setMarkingUnderOffer(true);
    try {
      await axios.patch(`${API}/listings/${listing.id}/status`, { status: 'under_offer' });
      toast.success(language === 'nl' ? 'Advertentie gemarkeerd als "Onder Bod"!' : 'Listing marked as "Under Offer"!');
      fetchListing();
    } catch (error) {
      console.error('Failed to mark as under offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setMarkingUnderOffer(false);
    }
  };

  // Handler for removing "Under Offer" status
  const handleUnmarkUnderOffer = async () => {
    setMarkingUnderOffer(true);
    try {
      await axios.patch(`${API}/listings/${listing.id}/status`, { status: 'available' });
      toast.success(language === 'nl' ? 'Onder Bod status verwijderd!' : 'Under Offer status removed!');
      fetchListing();
    } catch (error) {
      console.error('Failed to remove under offer status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setMarkingUnderOffer(false);
    }
  };

  // Handle price reduction confirmation
  const handleConfirmPriceReduction = async (newPrice) => {
    setReducingPrice(true);
    try {
      const response = await axios.patch(`${API}/listings/${listing.id}/reduce-price`, {
        new_price: newPrice
      });
      
      toast.success(
        language === 'nl' 
          ? `Prijs verlaagd naar ${response.data.currency} ${newPrice.toLocaleString()}!`
          : `Price reduced to ${response.data.currency} ${newPrice.toLocaleString()}!`
      );
      
      setShowPriceReductionModal(false);
      fetchListing();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reduce price';
      toast.error(language === 'nl' ? `Kon prijs niet verlagen: ${errorMsg}` : errorMsg);
    } finally {
      setReducingPrice(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="listing-detail-page">
      <Navbar />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation Row - Back button (mobile) + Breadcrumbs (desktop) */}
        <div className="flex h-10 items-center mb-6" data-testid="navigation-row">
          <Breadcrumbs 
            items={[
              { label: 'Home', href: '/' },
              { 
                label: getCategoryTranslation(listing.category, language) || formatCategorySlug(listing.category) || 'All Categories', 
                href: `/marketplace?category=${encodeURIComponent(listing.category || '')}` 
              },
              ...(listing.subcategory ? [{
                label: getSubcategoryTranslation(listing.subcategory, language),
                href: `/marketplace?category=${encodeURIComponent(listing.category || '')}&subcategory=${encodeURIComponent(listing.subcategory)}`
              }] : []),
              { label: listing.title || '' } // Last item - no href, shown as span
            ]}
            showBackButton={true}
            data-testid="breadcrumbs"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="image-gallery mb-6" data-testid="image-gallery">
              <div 
                className="gallery-main relative overflow-hidden rounded-2xl bg-black/5 dark:bg-white/5"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <img
                  src={images[selectedImage]}
                  alt={listing.title}
                  className="w-full h-full object-contain cursor-zoom-in select-none"
                  onClick={() => setShowImageModal(true)}
                  onError={(e) => {
                    e.target.src = defaultImage;
                  }}
                  draggable={false}
                />
                {/* Zoom hint */}
                <button 
                  onClick={() => setShowImageModal(true)}
                  className="absolute bottom-4 right-4 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-colors"
                  title="View full image"
                >
                  <ZoomIn className="w-5 h-5 text-white" />
                </button>
                {images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1)); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-800" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1)); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-800" />
                    </button>
                  </>
                )}
                {/* Badges - hidden when sold/reserved to avoid overlap with ribbon */}
                {listing.status !== 'sold' && listing.status !== 'reserved' && (
                  <div className="absolute top-4 left-4 flex gap-2">
                    <ListingTypeBadge type={listing.listing_type} size="lg" />
                  </div>
                )}
                
                {/* SOLD Ribbon */}
                {listing.status === 'sold' && (
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-10">
                    <div className="absolute -left-16 top-8 w-56 bg-red-600 text-white text-center py-2 font-bold text-lg uppercase tracking-wider shadow-lg transform -rotate-45">
                      Sold
                    </div>
                  </div>
                )}
                
                {/* RESERVED Ribbon */}
                {listing.status === 'reserved' && (
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-10">
                    <div className="absolute -left-16 top-8 w-56 bg-orange-500 text-white text-center py-2 font-bold text-lg uppercase tracking-wider shadow-lg transform -rotate-45">
                      Reserved
                    </div>
                  </div>
                )}
                
                {/* UNDER OFFER Ribbon */}
                {listing.status === 'under_offer' && (
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-10">
                    <div className="absolute -left-16 top-8 w-56 bg-amber-500 text-white text-center py-2 font-bold text-lg uppercase tracking-wider shadow-lg transform -rotate-45">
                      {language === 'nl' ? 'Onder Bod' : 'Under Offer'}
                    </div>
                    <div className="absolute inset-0 bg-black/10" />
                  </div>
                )}
                
                {/* Mobile swipe indicator dots */}
                {images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 lg:hidden z-10">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => { e.stopPropagation(); setSelectedImage(index); }}
                        className={`w-2 h-2 rounded-full transition-all ${
                          selectedImage === index 
                            ? 'bg-white w-4' 
                            : 'bg-white/50'
                        }`}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Thumbnails - includes video thumbnail if exists */}
              {(images.length > 1 || listing.video || listing.youtube_url) && (
                <div className="gallery-thumbs">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`gallery-thumb ${selectedImage === index ? 'active' : ''}`}
                    >
                      <img src={img} alt={`${listing.title} ${index + 1}`} />
                    </button>
                  ))}
                  {/* Video thumbnail - uploaded video */}
                  {listing.video && (
                    <button
                      onClick={() => setShowVideoModal(true)}
                      className="gallery-thumb relative group"
                      data-testid="video-thumbnail-btn"
                    >
                      {listing.video_thumbnail ? (
                        <img src={listing.video_thumbnail} alt="Video" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <Play className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      {/* Play icon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/50 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-5 h-5 text-primary fill-primary ml-0.5" />
                        </div>
                      </div>
                    </button>
                  )}
                  {/* YouTube video thumbnail */}
                  {listing.youtube_url && !listing.video && (
                    <button
                      onClick={() => setShowVideoModal(true)}
                      className="gallery-thumb relative group"
                      data-testid="youtube-thumbnail-btn"
                    >
                      <img 
                        src={`https://img.youtube.com/vi/${listing.youtube_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]}/mqdefault.jpg`}
                        alt="YouTube Video" 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = ''; e.target.parentElement.style.background = '#1a1a1a'; }}
                      />
                      {/* YouTube play icon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/50 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
                          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Video Modal - supports both uploaded video and YouTube */}
            {showVideoModal && (listing.video || listing.youtube_url) && (
              <div 
                className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                onClick={() => setShowVideoModal(false)}
              >
                <div 
                  className="relative w-full max-w-4xl max-h-[90vh] bg-card rounded-2xl overflow-hidden shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close button */}
                  <button
                    onClick={() => setShowVideoModal(false)}
                    className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                    data-testid="close-video-modal-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  {/* Video player - YouTube or uploaded */}
                  <div className="aspect-video">
                    {listing.youtube_url ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${listing.youtube_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]}?autoplay=1&rel=0`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="YouTube video"
                        data-testid="youtube-modal-player"
                      />
                    ) : (
                      <video 
                        src={listing.video}
                        className="w-full h-full object-contain bg-black"
                        controls
                        autoPlay
                        poster={listing.video_thumbnail}
                        data-testid="video-modal-player"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Key Info Card - Show price, title, seller contact RIGHT after images on mobile */}
            <div className="lg:hidden bg-card rounded-2xl p-4 shadow-lg border border-border mb-6">
              {/* Title & Price */}
              <h1 className="font-bold text-xl text-foreground mb-2" data-testid="mobile-listing-title">{listing.title}</h1>
              <div className="flex items-center gap-3 mb-4">
                {/* Price with original price struck through if reduced */}
                {listing.pricing?.original_price && listing.pricing.original_price > listing.pricing.amount ? (
                  <div className="flex flex-col">
                    <span className="text-sm text-muted-foreground line-through">
                      <PriceDisplay 
                        amount={listing.pricing.original_price}
                        fromCurrency={listing.pricing?.currency || 'USD'}
                        size="sm"
                      />
                    </span>
                    <PriceDisplay 
                      amount={listing.pricing?.amount || listing.price}
                      fromCurrency={listing.pricing?.currency || 'USD'}
                      size="lg"
                      className="text-green-600 dark:text-green-500"
                    />
                  </div>
                ) : (
                  <PriceDisplay 
                    amount={listing.pricing?.amount || listing.price}
                    fromCurrency={listing.pricing?.currency || 'USD'}
                    size="lg"
                    unit={listing.pricing?.price_unit}
                  />
                )}
                {listing.listing_type === 'rent' && (
                  <span className="text-sm text-muted-foreground">
                    / {getRentalPeriodLabel(listing.pricing?.rental_period || (listing.pricing?.is_hourly ? 'hourly' : 'monthly'), language)}
                  </span>
                )}
                {listing.pricing?.bidding_allowed && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-lg border border-amber-500/30">
                    <Gavel className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-amber-500">
                      {listing.bid_count > 0 ? `${listing.bid_count} bid${listing.bid_count !== 1 ? 's' : ''}` : 'Open for Bids'}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Auction Timer on Mobile */}
              {listing.listing_type === 'auction' && (listing.auction?.auction_end_time || listing.auction?.end_time) && (
                <div className="mb-4">
                  <AuctionTimer endTime={listing.auction.auction_end_time || listing.auction.end_time} compact={true} />
                </div>
              )}
              
              {/* Quick Actions Row */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
                <FavoriteButton listingId={listing.id} size="sm" />
                <button 
                  onClick={togglePriceAlert}
                  disabled={alertLoading}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                    hasAlert 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-secondary text-muted-foreground hover:text-amber-500'
                  }`}
                  title={hasAlert ? 'Remove price alert' : 'Get notified on price changes'}
                >
                  {alertLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : hasAlert ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                </button>
                <button 
                  onClick={() => setShowShareModal(true)}
                  className="w-9 h-9 bg-secondary rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                <span className="ml-auto text-xs text-muted-foreground flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5" /> {listing.views || 0}
                  </span>
                  {(listing.favorites > 0 || listing.favorite_count > 0) && (
                    <span className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-amber-500" /> {listing.favorites || listing.favorite_count || 0}
                    </span>
                  )}
                </span>
              </div>
              
              {/* Seller Info Compact */}
              <div className="flex items-center gap-3 mb-4 p-3 bg-secondary/30 rounded-xl">
                <div className="avatar w-11 h-11 text-base shrink-0">
                  {listing.user_avatar ? (
                    <img src={listing.user_avatar} alt={listing.user_name} className="w-full h-full object-cover" />
                  ) : (
                    listing.user_name?.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/seller/${listing.user_id}`} className="font-semibold text-foreground hover:text-primary">
                      {listing.user_name}
                    </Link>
                    {listing.is_verified_business && (
                      <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <TrustScoreBadge userId={listing.user_id} size="sm" />
                  </div>
                </div>
              </div>
              
              {/* Contact Buttons - Mobile */}
              {/* Show CTA button prominently if business listing has CTA URL */}
              {listing.cta_url && listing.is_verified_business ? (
                <div className="mb-4 space-y-2">
                  {/* Primary CTA Button */}
                  <a
                    href={normalizeUrl(listing.cta_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3.5 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-primary/25 transition-all"
                    data-testid="mobile-cta-button"
                  >
                    <ExternalLink className="w-5 h-5" />
                    {listing.cta_type === 'event' ? t('cta_view_event') : 
                     listing.cta_type === 'sale' ? t('cta_view_sale') : 
                     t('cta_view_promo')}
                  </a>
                  {/* Campaign Date Indicators */}
                  {(listing.campaign_start || listing.campaign_end) && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl border border-primary/20">
                      <div className="flex flex-col gap-2">
                        {/* Start date - show if in future */}
                        {listing.campaign_start && new Date(listing.campaign_start) > new Date() && (
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                              <Calendar className="w-4 h-4 text-amber-600" />
                            </div>
                            <div>
                              <span className="text-muted-foreground text-xs">{t('campaign_starts')}</span>
                              <p className="font-medium text-foreground">
                                {new Date(listing.campaign_start).toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'es' ? 'es-ES' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        )}
                        {/* Currently running indicator */}
                        {listing.campaign_start && new Date(listing.campaign_start) <= new Date() && listing.campaign_end && new Date(listing.campaign_end) > new Date() && (
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            </div>
                            <span className="font-medium text-green-600">{t('campaign_running')}</span>
                          </div>
                        )}
                        {/* End date */}
                        {listing.campaign_end && (
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                              <Clock className="w-4 h-4 text-red-500" />
                            </div>
                            <div>
                              <span className="text-muted-foreground text-xs">{t('campaign_ends')}</span>
                              <p className="font-medium text-foreground">
                                {new Date(listing.campaign_end).toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'es' ? 'es-ES' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {listing.phone && (
                    <a 
                      href={`tel:${listing.phone}`}
                      className="btn btn-secondary text-sm py-2.5 flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4" /> Call
                    </a>
                  )}
                  {listing.whatsapp && (
                    <a 
                      href={`https://wa.me/${listing.whatsapp}?text=Hi, I'm interested in: ${listing.title}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn bg-green-600 hover:bg-green-700 text-white text-sm py-2.5 flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                  )}
                  {user && user.id !== listing.user_id && (
                    <button
                      onClick={() => setShowChat(true)}
                      className="btn btn-primary text-sm py-2.5 flex items-center justify-center gap-2 col-span-2"
                    >
                      <Send className="w-4 h-4" /> Send Message
                    </button>
                  )}
                </div>
              )}
              
              {/* Contact Info - Mobile */}
              {(listing.contact_email || listing.contact_phone || listing.user_whatsapp) && (
                <div className="pt-3 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Contact</p>
                  <div className="flex flex-wrap gap-2">
                    {listing.user_whatsapp && (
                      <a 
                        href={`https://wa.me/${listing.user_whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-green-500/10 rounded-lg hover:bg-green-500/20 transition-colors"
                        data-testid="mobile-whatsapp-contact-btn"
                      >
                        <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <span className="text-sm text-foreground">WhatsApp</span>
                      </a>
                    )}
                    {listing.contact_phone && (
                      <a 
                        href={`tel:${listing.contact_phone}`}
                        className="flex items-center gap-2 px-3 py-2 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                        data-testid="mobile-call-contact-btn"
                      >
                        <Phone className="w-4 h-4 text-primary" />
                        <span className="text-sm text-foreground">{listing.contact_phone}</span>
                      </a>
                    )}
                    {listing.contact_email && (
                      <a 
                        href={`mailto:${listing.contact_email}`}
                        className="flex items-center gap-2 px-3 py-2 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                        data-testid="mobile-email-contact-btn"
                      >
                        <Mail className="w-4 h-4 text-primary" />
                        <span className="text-sm text-foreground truncate max-w-[140px]">{listing.contact_email}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              {/* Location - Mobile (clickable to open map modal) */}
              <div className="pt-3 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t('location')}</p>
                <button
                  onClick={() => setShowMobileLocationModal(true)}
                  className="w-full flex items-center gap-3 p-3 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors text-left"
                  data-testid="mobile-location-btn"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {(() => {
                      const loc = typeof listing.location === 'object' ? listing.location : {};
                      const hasLocation = loc.city || loc.area || loc.address || loc.island || loc.country;
                      if (hasLocation) {
                        const locationText = [loc.city, loc.area, loc.island || loc.country].filter(Boolean).join(', ');
                        return (
                          <>
                            <p className="text-sm font-medium text-foreground truncate">{locationText || t('view_location')}</p>
                            <p className="text-xs text-muted-foreground">{t('tap_to_view_map')}</p>
                          </>
                        );
                      }
                      return (
                        <>
                          <p className="text-sm font-medium text-foreground">{t('location_on_request')}</p>
                          <p className="text-xs text-muted-foreground">{t('contact_seller')}</p>
                        </>
                      );
                    })()}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                </button>
              </div>
              
              {/* Owner Controls - Mobile */}
              {isOwner && (
                <div className="pt-3 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    {language === 'nl' ? 'Beheer Advertentie' : 'Manage Listing'}
                  </p>
                  
                  {/* Status-based controls */}
                  {listing.status === 'sold' ? (
                    <div className="space-y-2">
                      <div className="w-full py-2.5 bg-red-500/10 text-red-600 rounded-xl font-semibold flex items-center justify-center gap-2 border border-red-500/20 text-sm">
                        <Check className="w-4 h-4" />
                        {language === 'nl' ? 'Verkocht' : 'Marked as Sold'}
                      </div>
                      <button
                        onClick={handleUnmarkSold}
                        className="w-full py-2 text-xs text-muted-foreground hover:text-foreground border border-border rounded-xl transition-colors"
                        data-testid="mobile-unmark-sold-btn"
                      >
                        {language === 'nl' ? 'Heractiveren' : 'Reactivate Listing'}
                      </button>
                    </div>
                  ) : listing.status === 'reserved' ? (
                    <div className="space-y-2">
                      <div className="w-full py-2.5 bg-orange-500/10 text-orange-600 rounded-xl font-semibold flex items-center justify-center gap-2 border border-orange-500/20 text-sm">
                        <Clock className="w-4 h-4" />
                        {language === 'nl' ? 'Gereserveerd' : 'Marked as Reserved'}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleOpenMarkSoldModal}
                          className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium text-xs flex items-center justify-center gap-1 transition-colors"
                          data-testid="mobile-mark-sold-btn"
                        >
                          <ShoppingBag className="w-3.5 h-3.5" />
                          {language === 'nl' ? 'Verkocht' : 'Mark Sold'}
                        </button>
                        <button
                          onClick={handleUnmarkReserved}
                          className="flex-1 py-2 text-xs text-muted-foreground hover:text-foreground border border-border rounded-xl transition-colors"
                          data-testid="mobile-unmark-reserved-btn"
                        >
                          {language === 'nl' ? 'Verwijderen' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  ) : listing.status === 'under_offer' ? (
                    <div className="space-y-2">
                      <div className="w-full py-2.5 bg-amber-500/10 text-amber-600 rounded-xl font-semibold flex items-center justify-center gap-2 border border-amber-500/20 text-sm">
                        <FileCheck className="w-4 h-4" />
                        {language === 'nl' ? 'Onder Bod' : 'Under Offer'}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleOpenMarkSoldModal}
                          className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium text-xs flex items-center justify-center gap-1 transition-colors"
                          data-testid="mobile-mark-sold-btn"
                        >
                          <ShoppingBag className="w-3.5 h-3.5" />
                          {language === 'nl' ? 'Verkocht' : 'Mark Sold'}
                        </button>
                        <button
                          onClick={handleUnmarkUnderOffer}
                          disabled={markingUnderOffer}
                          className="flex-1 py-2 text-xs text-muted-foreground hover:text-foreground border border-border rounded-xl transition-colors disabled:opacity-50"
                          data-testid="mobile-unmark-under-offer-btn"
                        >
                          {markingUnderOffer ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : (language === 'nl' ? 'Verwijderen' : 'Remove')}
                        </button>
                      </div>
                      {/* Price reduction still available while under offer */}
                      <button
                        onClick={() => setShowPriceReductionModal(true)}
                        className="w-full py-2 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-medium text-xs flex items-center justify-center gap-1.5 transition-colors"
                        data-testid="mobile-reduce-price-btn"
                      >
                        <TrendingDown className="w-3.5 h-3.5 text-emerald-500" />
                        {language === 'nl' ? 'Prijs Verlagen' : 'Reduce Price'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Grid 2x2: Professional, refined UI */}
                      <div className="grid grid-cols-2 gap-2">
                        {/* Sold - Primary action */}
                        <button
                          onClick={handleOpenMarkSoldModal}
                          className="py-2.5 bg-zinc-900 dark:bg-zinc-100 text-zinc-50 dark:text-zinc-900 rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all text-sm shadow-sm hover:opacity-90"
                          data-testid="mobile-mark-sold-btn"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          {language === 'nl' ? 'Verkocht' : 'Sold'}
                        </button>
                        {/* Reserved - Secondary */}
                        <button
                          onClick={handleMarkAsReserved}
                          className="py-2.5 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all text-sm"
                          data-testid="mobile-mark-reserved-btn"
                        >
                          <Clock className="w-4 h-4 text-orange-500" />
                          {language === 'nl' ? 'Gereserveerd' : 'Reserved'}
                        </button>
                        {/* Under Offer - Secondary */}
                        <button
                          onClick={handleMarkUnderOffer}
                          disabled={markingUnderOffer}
                          className="py-2.5 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all text-sm disabled:opacity-50"
                          data-testid="mobile-mark-under-offer-btn"
                        >
                          {markingUnderOffer ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4 text-amber-500" />}
                          {language === 'nl' ? 'Onder Bod' : 'Under Offer'}
                        </button>
                        {/* Reduce Price - Functional */}
                        <button
                          onClick={() => setShowPriceReductionModal(true)}
                          className="py-2.5 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all text-sm"
                          data-testid="mobile-reduce-price-btn"
                        >
                          <TrendingDown className="w-4 h-4 text-emerald-500" />
                          {language === 'nl' ? 'Verlaagd' : 'Reduce'}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <Link
                    to={`/edit-listing/${listing.id}`}
                    className="block w-full py-2.5 btn-outline text-center text-sm mt-2"
                    data-testid="mobile-edit-listing-btn"
                  >
                    {language === 'nl' ? 'Bewerken' : 'Edit Listing'}
                  </Link>
                </div>
              )}
            </div>

            {/* Listing Info */}
            <div className="bg-card rounded-2xl shadow-lg border border-border overflow-hidden">
              {/* Category badge and metadata - Always visible header */}
              <div className="p-4 md:p-6 border-b border-border">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="badge badge-category capitalize">
                    {listing.category.replace('-', ' ')}
                  </span>
                  {/* Island Badge */}
                  {(() => {
                    const loc = typeof listing.location === 'object' ? listing.location : {};
                    // Check visible_islands first, then fall back to location data
                    const islandFromArray = listing.visible_islands?.[0];
                    const island = islandFromArray || loc.island || loc.country || loc.area || '';
                    const islandLower = island.toLowerCase();
                    
                    if (islandLower.includes('aruba')) {
                      return (
                        <span className="px-2.5 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                          Aruba
                        </span>
                      );
                    } else if (islandLower.includes('bonaire')) {
                      return (
                        <span className="px-2.5 py-1 bg-yellow-500 text-black text-xs font-bold rounded-full">
                          Bonaire
                        </span>
                      );
                    } else if (islandLower.includes('cura')) {
                      return (
                        <span className="px-2.5 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                          Curaçao
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Location, Date, Views - Compact metadata row */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span>{(() => {
                      const loc = typeof listing.location === 'object' ? listing.location : {};
                      const ISLAND_NAMES = { aruba: 'Aruba', curacao: 'Curaçao', bonaire: 'Bonaire' };
                      return loc.city || loc.area || 
                        (listing.visible_islands?.length === 1 ? ISLAND_NAMES[listing.visible_islands[0]] || listing.visible_islands[0] : null) ||
                        loc.country || 'Caribbean';
                    })()}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span>Listed {formatDate(listing.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Eye className="w-4 h-4 text-primary" />
                    <span>{listing.views || 0} views</span>
                  </div>
                  {(listing.favorites > 0 || listing.favorite_count > 0) && (
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4 text-amber-500" />
                      <span>{listing.favorites || listing.favorite_count || 0} saved</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Main Content */}
              <div className="p-4 md:p-6">
              {listing.listing_type === 'auction' && (
                <div className="mb-8 flex flex-col gap-4">
                  {/* ═══════════════════════════════════════════════════════════
                      AUCTION STATUS CARD - Current Bid + Timer + Winning Status
                      ═══════════════════════════════════════════════════════════ */}
                  <div className="p-6 bg-card rounded-2xl border border-border" data-testid="auction-status-card">
                    {/* Grid: Current Bid | Timer */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-4">
                      {/* Left: Current Bid */}
                      <div className="md:border-r md:border-border md:pr-8">
                        <div className="flex items-center gap-2 mb-2">
                          <Gavel className="w-5 h-5 text-rose-500" />
                          <span className="font-medium text-muted-foreground text-sm">
                            {listing.auction?.current_bid ? 'Current Bid' : 'Starting Bid'}
                          </span>
                          {listing.auction?.bid_count > 0 && (
                            <span className="ml-auto text-xs bg-rose-500/20 text-rose-400 px-2 py-0.5 rounded-full">
                              {listing.auction.bid_count} bids
                            </span>
                          )}
                        </div>
                        <PriceDisplay 
                          amount={listing.auction?.current_bid || listing.auction?.starting_price || 0}
                          size="xl"
                          showBoth={true}
                        />
                        {listing.auction?.current_bidder_name && (
                          <p className="text-sm text-muted-foreground mt-2">
                            Leading: <span className="text-foreground font-medium">{listing.auction.current_bidder_name}</span>
                          </p>
                        )}
                      </div>

                      {/* Right: Timer with Progress Bar */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-5 h-5 text-blue-500" />
                          <span className="font-medium text-muted-foreground text-sm">Time Remaining</span>
                        </div>
                        {(listing.auction?.auction_end_time || listing.auction?.end_time) && (() => {
                          const endTime = new Date(listing.auction.auction_end_time || listing.auction.end_time);
                          const now = new Date();
                          const totalDuration = 7 * 24 * 60 * 60 * 1000; // Assume 7 days total
                          const remaining = Math.max(0, endTime - now);
                          const elapsed = totalDuration - remaining;
                          const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
                          const isUrgent = remaining < 60 * 60 * 1000; // Less than 1 hour
                          const isWarning = remaining < 24 * 60 * 60 * 1000; // Less than 24 hours
                          
                          const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
                          const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                          const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                          const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

                          return (
                            <>
                              <div className={`text-2xl font-bold mb-3 ${isUrgent ? 'text-red-500' : isWarning ? 'text-orange-500' : 'text-foreground'}`}>
                                {days > 0 && <span>{days}d </span>}
                                <span>{String(hours).padStart(2, '0')}h </span>
                                <span>{String(minutes).padStart(2, '0')}m </span>
                                <span>{String(seconds).padStart(2, '0')}s</span>
                              </div>
                              {/* Progress Bar */}
                              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all duration-1000 ${
                                    isUrgent ? 'bg-gradient-to-r from-red-500 to-red-600' : 
                                    isWarning ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 
                                    'bg-gradient-to-r from-blue-500 to-cyan-500'
                                  }`}
                                  style={{ width: `${100 - progressPercent}%` }}
                                />
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {isUrgent ? '⚡ Ending soon!' : isWarning ? '⏰ Less than 24h left' : 'Auction in progress'}
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Winner Banner - Only shows if user is highest bidder */}
                    {user && listing.auction?.current_bidder_id === user.id && (
                      <div className="p-4 bg-gradient-to-r from-green-500/15 to-emerald-500/15 rounded-xl border border-green-500/30 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-400/20 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
                        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/30">
                              <Trophy className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-bold text-foreground">You're Winning!</p>
                              <p className="text-sm text-muted-foreground">Your bid: <PriceDisplay amount={listing.auction?.current_bid || 0} size="sm" showBoth={false} /></p>
                            </div>
                          </div>
                          {new Date(listing.auction?.end_time || listing.auction?.auction_end_time) > new Date() && (
                            <button
                              onClick={handleWithdrawBid}
                              disabled={withdrawingBid}
                              className="w-full sm:w-auto px-3 py-2 bg-red-500/10 text-red-500 hover:bg-red-500/20 font-medium rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                              data-testid="withdraw-bid-btn"
                            >
                              {withdrawingBid ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                              Withdraw
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ═══════════════════════════════════════════════════════════
                      AUCTION ACTION CARD - Place Bid Form + Bid History Accordion
                      ═══════════════════════════════════════════════════════════ */}
                  {(listing.auction?.auction_end_time || listing.auction?.end_time) && new Date(listing.auction.auction_end_time || listing.auction.end_time) > new Date() && (
                    <div className="p-6 bg-card rounded-2xl border border-border" data-testid="auction-action-card">
                      {/* Place Bid Form - Only show if not owner */}
                      {listing.user_id !== user?.id && (
                        <>
                          <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-500" />
                            Place Your Bid
                          </h4>
                          {/* Responsive: Side by side on desktop, stacked on mobile */}
                          <div className="flex flex-col sm:flex-row gap-3 mb-2">
                            <div className="relative flex-1">
                              <input
                                type="number"
                                value={bidAmount}
                                onChange={(e) => setBidAmount(e.target.value)}
                                placeholder={(() => {
                                  const minBidUSD = Math.max(listing.auction?.starting_price || 0, (listing.auction?.current_bid || 0) + (listing.auction?.bid_increment || 1));
                                  if (currency === 'XCG') {
                                    return `Min: XCG ${(minBidUSD * USD_TO_XCG).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`;
                                  }
                                  return `Min: $ ${minBidUSD.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`;
                                })()}
                                className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                data-testid="auction-bid-input"
                              />
                            </div>
                            <button
                              onClick={handlePlaceBid}
                              disabled={placingBid}
                              className="px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 sm:w-auto w-full"
                              data-testid="auction-place-bid-btn"
                            >
                              {placingBid ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : (
                                <>
                                  <Gavel className="w-5 h-5" />
                                  Bid Now
                                </>
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground mb-4">
                            Minimum increment: {currency === 'XCG' 
                              ? `XCG ${((listing.auction?.bid_increment || 1) * USD_TO_XCG).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`
                              : `$ ${(listing.auction?.bid_increment || 1).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`
                            }
                          </p>
                        </>
                      )}

                      {/* Bid History Accordion */}
                      {listing.auction?.bids && listing.auction.bids.length > 0 && (
                        <>
                          {listing.user_id !== user?.id && <div className="border-t border-border my-4" />}
                          <button
                            onClick={() => setShowBidHistory(!showBidHistory)}
                            className="w-full flex items-center justify-between text-foreground py-2"
                            data-testid="toggle-bid-history"
                          >
                            <span className="flex items-center gap-2 font-medium">
                              <History className="w-5 h-5 text-blue-500" />
                              Bid History
                              <span className="text-xs bg-secondary px-2 py-0.5 rounded-full text-muted-foreground">
                                {listing.auction.bids.length} {listing.auction.bids.length === 1 ? 'bid' : 'bids'}
                              </span>
                            </span>
                            <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${showBidHistory ? 'rotate-90' : ''}`} />
                          </button>
                          
                          {/* Collapsible Bid List */}
                          <div className={`overflow-hidden transition-all duration-300 ${showBidHistory ? 'max-h-64 mt-3' : 'max-h-0'}`}>
                            <div className="space-y-2 overflow-y-auto max-h-56">
                              {[...listing.auction.bids].reverse().map((bid, index) => (
                                <div 
                                  key={bid.id || index}
                                  className={`flex items-center justify-between p-3 rounded-lg ${index === 0 ? 'bg-green-500/10 border border-green-500/20' : 'bg-secondary/50'}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${index === 0 ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-slate-500 to-slate-600'}`}>
                                      {bid.bidder_name?.[0] || 'U'}
                                    </div>
                                    <div>
                                      <p className="font-medium text-foreground text-sm flex items-center gap-2">
                                        {bid.bidder_name}
                                        {index === 0 && (
                                          <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded font-medium">
                                            Leading
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {new Date(bid.timestamp).toLocaleString('nl-NL', { 
                                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                                        })}
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`font-bold text-sm ${index === 0 ? 'text-green-500' : 'text-foreground'}`}>
                                    <PriceDisplay amount={bid.amount} size="sm" />
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Bid Feed Section - For listings with bidding enabled */}
              {listing.pricing?.bidding_allowed && listing.listing_type === 'sale' && (
                <div className="mb-8">
                  <BidFeed
                    listingId={listing.id}
                    sellerId={listing.user_id}
                    minimumBid={listing.pricing?.minimum_bid || 0}
                    currency={listing.pricing?.currency || 'USD'}
                    onBidPlaced={() => {
                      // Refresh listing to get updated bid count
                      fetchListing();
                    }}
                    onBidChange={() => {
                      // Refresh listing when bid is placed, withdrawn, accepted, or rejected
                      fetchListing();
                    }}
                  />
                </div>
              )}
              
              {/* Offer/Bid History Section - For Yard Sales and Bulk Sales */}
              {['yard_sale', 'bulk'].includes(listing.listing_type) && (
                <div className="mb-8 space-y-10">
                  {/* Master Buyout Card - Show first for prominence */}
                  {listing.yard_sale?.items && listing.yard_sale.items.length > 0 && (
                    <div className="p-6 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-rose-500/10 rounded-2xl border-2 border-amber-500/30 shadow-lg" data-testid="master-buyout-card">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                            <ShoppingBag className="w-6 h-6 text-amber-500" />
                            Buy Everything
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {listing.yard_sale.items.filter(i => i.status !== 'sold').length} items available
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground line-through">
                            {formatCurrency(
                              listing.yard_sale.items.reduce((sum, i) => sum + (i.price * (i.quantity || 1)), 0),
                              listing.yard_sale.items[0]?.currency || 'USD'
                            )}
                          </p>
                          <p className="text-3xl font-bold text-amber-500">
                            {formatCurrency(
                              listing.yard_sale.bundle_price || 
                              listing.yard_sale.items.reduce((sum, i) => sum + (i.price * (i.quantity || 1)), 0) * (1 - (listing.yard_sale.bundle_discount_percent || 0) / 100),
                              listing.yard_sale.items[0]?.currency || 'USD'
                            )}
                          </p>
                          {(listing.yard_sale.bundle_price || listing.yard_sale.bundle_discount_percent) && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
                              SAVE {listing.yard_sale.bundle_discount_percent || Math.round((1 - (listing.yard_sale.bundle_price / listing.yard_sale.items.reduce((sum, i) => sum + (i.price * (i.quantity || 1)), 0))) * 100)}%
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* WhatsApp Bundle Inquiry */}
                      {listing.user_whatsapp && listing.user_id !== user?.id && (
                        <a
                          href={`https://wa.me/${listing.user_whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi! I'm interested in buying ALL items from your yard sale "${listing.title}" for the bundle price. Is this still available?`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-500/20"
                          data-testid="bundle-whatsapp-btn"
                        >
                          <MessageCircle className="w-5 h-5" />
                          <span className="hidden sm:inline">Contact for Bundle Deal</span>
                          <span className="sm:hidden">Bundle Deal</span>
                        </a>
                      )}
                      
                      {/* Make Offer Button - for buyers */}
                      {listing.user_id !== user?.id && (
                        <button
                          onClick={() => setShowMakeOffer(!showMakeOffer)}
                          className="w-full mt-3 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                          data-testid="make-offer-toggle-btn"
                        >
                          <DollarSign className="w-5 h-5" />
                          {language === 'nl' ? 'Doe een bod' : 'Make an Offer'}
                        </button>
                      )}
                      
                      {/* Make Offer Form */}
                      {showMakeOffer && listing.user_id !== user?.id && (() => {
                        // Use the user's selected display currency
                        const displayCurrencySymbol = currency === 'XCG' ? 'XCG' : '$';
                        return (
                        <div className="mt-4 p-4 bg-card rounded-xl border border-border space-y-3" data-testid="make-offer-form">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                              {language === 'nl' ? 'Jouw bod' : 'Your Offer'} ({displayCurrencySymbol})
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                {displayCurrencySymbol}
                              </span>
                              <input
                                type="number"
                                value={offerAmount}
                                onChange={(e) => setOfferAmount(e.target.value)}
                                placeholder={language === 'nl' ? 'Voer bedrag in' : 'Enter amount'}
                                className="w-full pl-12 pr-3 py-2.5 rounded-xl border border-input bg-background text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                data-testid="offer-amount-input"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleSubmitOffer}
                              disabled={submittingOffer || !offerAmount}
                              className="flex-1 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid="submit-offer-btn"
                            >
                              {submittingOffer ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                              {language === 'nl' ? 'Verstuur Bod' : 'Submit Offer'}
                            </button>
                            <button
                              onClick={() => {
                                setShowMakeOffer(false);
                                setOfferAmount('');
                              }}
                              className="px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-xl font-medium transition-all"
                            >
                              {language === 'nl' ? 'Annuleren' : 'Cancel'}
                            </button>
                          </div>
                        </div>
                      );})()}
                      
                      {/* Offer History - integrated with Make Offer section */}
                      <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border">
                        <button
                          onClick={() => setShowOfferHistory(!showOfferHistory)}
                          className="w-full flex items-center justify-between text-foreground font-medium text-sm"
                          data-testid="toggle-offer-history"
                        >
                          <span className="flex items-center gap-2">
                            <History className="w-4 h-4 text-muted-foreground" />
                            {language === 'nl' ? 'Bod Geschiedenis' : 'Offer History'} ({offerHistory.length})
                          </span>
                          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showOfferHistory ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {showOfferHistory && (() => {
                          // Get the native currency of the yard sale for conversion
                          const ysCurrency = listing.yard_sale?.currency || listing.yard_sale?.items?.[0]?.currency || 'USD';
                          return (
                          <div className="mt-3 space-y-2">
                            {loadingOffers ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                              </div>
                            ) : offerHistory.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-3">
                                {language === 'nl' ? 'Nog geen biedingen.' : 'No offers yet.'}
                              </p>
                            ) : (
                              offerHistory.slice(0, 5).map((offer, index) => {
                                const isMyOffer = user && (offer.bidder_id === user.id || offer.buyer_id === user.id);
                                const isPending = offer.status === 'pending';
                                const offerAmount = offer.amount || offer.offer_amount;
                                const offerName = offer.bidder_name || offer.buyer_name || 'Anonymous';
                                return (
                                <div key={offer.id || index} className="flex items-center justify-between p-2 bg-background rounded-lg text-sm gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                      <User className="w-3 h-3 text-primary" />
                                    </div>
                                    <span className="text-foreground truncate">{offerName}</span>
                                    {isMyOffer && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium flex-shrink-0">
                                        {language === 'nl' ? 'Jouw bod' : 'Your offer'}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="font-semibold text-primary">
                                      {formatCurrency(offerAmount, ysCurrency)}
                                    </span>
                                    {/* Withdraw button for user's own pending offers */}
                                    {isMyOffer && isPending && (
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleWithdrawOffer(offer.id);
                                        }}
                                        disabled={withdrawingOffer === offer.id}
                                        className="p-1 text-red-500 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                                        title={language === 'nl' ? 'Bod intrekken' : 'Withdraw offer'}
                                        data-testid={`withdraw-offer-${index}`}
                                      >
                                        {withdrawingOffer === offer.id ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <X className="w-3.5 h-3.5" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )})
                            )}
                          </div>
                        );})()}
                      </div>
                    </div>
                  )}
                  
                  {/* Visual Inventory Catalog Grid */}
                  {listing.yard_sale?.items && listing.yard_sale.items.length > 0 && (
                    <div data-testid="yard-sale-catalog">
                      {/* Separator line between Buy Everything and Catalog */}
                      <hr className="border-border my-8" />
                      
                      <h4 className="font-semibold text-foreground mb-6 flex items-center gap-2">
                        <Package className="w-5 h-5 text-amber-500" />
                        {listing.listing_type === 'yard_sale' ? 'Yard Sale' : 'Bundle'} Catalog ({listing.yard_sale.items.length} items)
                      </h4>
                      
                      {/* 2/3 Column Responsive Grid with more spacing */}
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                        {listing.yard_sale.items.map((item, index) => {
                          const isSold = item.status === 'sold';
                          const isReserved = item.status === 'reserved';
                          const itemCurrency = item.currency || 'USD';
                          
                          return (
                            <div 
                              key={index}
                              className={`group relative bg-card rounded-xl overflow-hidden border-2 transition-all hover:shadow-lg ${
                                isSold ? 'border-red-500/30 opacity-75' : 
                                isReserved ? 'border-yellow-500/30' : 
                                'border-border hover:border-amber-500/50'
                              }`}
                              data-testid={`yard-sale-item-card-${index}`}
                            >
                              {/* Item Image with Price Overlay */}
                              <div className="relative aspect-square bg-muted overflow-hidden">
                                {item.image ? (
                                  <button
                                    onClick={() => setYardSalePhotoModal({ image: item.image, name: item.name, index })}
                                    className="w-full h-full cursor-zoom-in"
                                    data-testid={`yard-sale-item-image-${index}`}
                                  >
                                    <img 
                                      src={item.image} 
                                      alt={item.name}
                                      className={`w-full h-full object-cover transition-transform group-hover:scale-105 ${isSold ? 'grayscale' : ''}`}
                                      loading="lazy"
                                    />
                                    {/* Zoom hint on hover */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                      <ZoomIn className="w-8 h-8 text-white drop-shadow-lg" />
                                    </div>
                                  </button>
                                ) : (
                                  <div className={`w-full h-full flex items-center justify-center ${isSold ? 'grayscale' : ''}`}>
                                    <Package className="w-12 h-12 text-muted-foreground/40" />
                                  </div>
                                )}
                                
                                {/* Price Tag Overlay */}
                                <div className={`absolute bottom-2 right-2 px-3 py-1.5 rounded-lg shadow-lg backdrop-blur-sm font-bold text-sm ${
                                  isSold ? 'bg-red-500/90 text-white' :
                                  isReserved ? 'bg-yellow-500/90 text-black' :
                                  'bg-background/90 text-foreground border border-border'
                                }`}>
                                  {isSold 
                                    ? (language === 'nl' ? 'VERKOCHT' : language === 'es' ? 'VENDIDO' : 'SOLD')
                                    : formatCurrency(item.price, itemCurrency)}
                                </div>
                                
                                {/* Status Badge - Top Left with glass effect */}
                                {isReserved && !isSold && (
                                  <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-500/90 backdrop-blur-sm text-black text-[10px] font-bold uppercase tracking-wide rounded-md shadow-lg">
                                    {language === 'nl' ? 'Gereserveerd' : language === 'es' ? 'Reservado' : 'Reserved'}
                                  </div>
                                )}
                                
                                {/* Quantity Badge */}
                                {item.quantity > 1 && !isSold && (
                                  <div className="absolute top-2 right-2 px-2 py-1 bg-primary text-white text-xs font-bold rounded-lg shadow-lg">
                                    x{item.quantity}
                                  </div>
                                )}
                              </div>
                              
                              {/* Item Details */}
                              <div className="p-3 min-h-[80px] flex flex-col">
                                <h5 className={`font-semibold text-sm truncate ${isSold ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                                  {item.name}
                                </h5>
                                {item.description && (
                                  <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                    {item.description}
                                  </p>
                                )}
                                
                                {/* Action Buttons for Buyers - Compact Row */}
                                {!isSold && listing.user_id !== user?.id && (
                                  <div className="mt-auto pt-2 flex items-center gap-1.5">
                                    {/* WhatsApp Per-Item Button */}
                                    {listing.yard_sale.enable_whatsapp_per_item !== false && listing.user_whatsapp && (
                                      <a
                                        href={`https://wa.me/${listing.user_whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi! I'm interested in "${item.name}" from your yard sale "${listing.title}" priced at ${formatCurrency(item.price, itemCurrency)}. Is this still available?`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 py-1.5 bg-green-500 hover:bg-green-600 text-white text-[11px] font-bold rounded-lg flex items-center justify-center gap-1 transition-colors"
                                        data-testid={`whatsapp-item-${index}`}
                                      >
                                        <MessageCircle className="w-3 h-3" />
                                        <span className="hidden sm:inline">WhatsApp</span>
                                      </a>
                                    )}
                                    
                                    {/* Message Seller Button - for individual purchases */}
                                    {listing.yard_sale.allow_individual_purchase && user && (
                                      <button
                                        onClick={() => {
                                          setNewMessage(`Hi! I'm interested in buying "${item.name}" (${formatCurrency(item.price, itemCurrency)}) from your yard sale. Is this item still available?`);
                                          setShowChat(true);
                                        }}
                                        className="flex-1 py-1.5 bg-primary hover:bg-primary/90 text-white text-[11px] font-bold rounded-lg flex items-center justify-center gap-1 transition-colors"
                                        data-testid={`message-item-${index}`}
                                      >
                                        <Mail className="w-3 h-3" />
                                        <span className="hidden sm:inline">{language === 'nl' ? 'Bericht' : 'Message'}</span>
                                      </button>
                                    )}
                                  </div>
                                )}
                                
                                {/* Seller Controls - Compact Horizontal Layout */}
                                {listing.user_id === user?.id && (
                                  <div className="mt-auto pt-2 flex items-center gap-1.5">
                                    {/* Available item: Reserve and Sold buttons side by side */}
                                    {!isSold && !isReserved && (
                                      <>
                                        <button
                                          onClick={() => handleItemStatusChange(index, 'reserved')}
                                          className="flex-1 py-1.5 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 text-[11px] font-bold rounded-lg flex items-center justify-center gap-1 transition-colors border border-yellow-500/30"
                                          data-testid={`reserve-item-${index}`}
                                          title={language === 'nl' ? 'Reserveren' : 'Reserve'}
                                        >
                                          <Clock className="w-3 h-3" />
                                          <span className="hidden sm:inline">{language === 'nl' ? 'Reserveer' : 'Reserve'}</span>
                                        </button>
                                        <button
                                          onClick={() => handleItemStatusChange(index, 'sold')}
                                          className="flex-1 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 text-[11px] font-bold rounded-lg flex items-center justify-center gap-1 transition-colors border border-red-500/30"
                                          data-testid={`mark-sold-item-${index}`}
                                          title={language === 'nl' ? 'Verkocht' : 'Sold'}
                                        >
                                          <Check className="w-3 h-3" />
                                          <span className="hidden sm:inline">{language === 'nl' ? 'Verkocht' : 'Sold'}</span>
                                        </button>
                                      </>
                                    )}
                                    
                                    {/* Reserved item: Primary Sold + Secondary Cancel (icon-only) */}
                                    {isReserved && (
                                      <>
                                        <button
                                          onClick={() => handleItemStatusChange(index, 'sold')}
                                          className="flex-1 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 text-[11px] font-bold rounded-lg flex items-center justify-center gap-1 transition-colors border border-red-500/30"
                                          data-testid={`mark-sold-item-${index}`}
                                        >
                                          <Check className="w-3 h-3" />
                                          {language === 'nl' ? 'VERKOCHT' : 'SOLD'}
                                        </button>
                                        <button
                                          onClick={() => handleItemStatusChange(index, 'available')}
                                          className="p-1.5 bg-muted hover:bg-muted/80 text-muted-foreground rounded-lg transition-colors border border-border"
                                          data-testid={`cancel-reserve-item-${index}`}
                                          title={language === 'nl' ? 'Annuleer Reservering' : 'Cancel Reservation'}
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                    
                                    {/* Sold item: Compact Undo button */}
                                    {isSold && (
                                      <button
                                        onClick={() => handleItemStatusChange(index, 'available')}
                                        className="flex-1 py-1.5 bg-muted hover:bg-muted/80 text-muted-foreground text-[11px] font-bold rounded-lg flex items-center justify-center gap-1 transition-colors"
                                        data-testid={`undo-sold-item-${index}`}
                                      >
                                        <RotateCcw className="w-3 h-3" />
                                        {language === 'nl' ? 'Herstel' : 'Undo'}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Individual Purchase Notice */}
                      {listing.yard_sale.allow_individual_purchase && (
                        <p className="text-xs text-muted-foreground mt-4 text-center flex items-center justify-center gap-1">
                          <Check className="w-3.5 h-3.5 text-green-500" />
                          Individual items can be purchased separately
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Trade Info */}
              {(listing.listing_type === 'trade' || listing.trade?.open_for_trade) && (
                <div className="mt-8 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
                  <h3 className="text-lg font-semibold text-emerald-700 dark:text-emerald-400 mb-3 flex items-center gap-2">
                    <ArrowLeftRight className="w-5 h-5" />
                    Open for Trade
                  </h3>
                  {listing.trade?.trade_value && (
                    <p className="text-emerald-600 dark:text-emerald-300 text-sm mb-2">
                      <span className="font-medium">Trade Value:</span> {formatCurrency(listing.trade.trade_value, listing.trade.trade_value_currency || 'USD')}
                    </p>
                  )}
                  {listing.trade?.trade_preferences && (
                    <p className="text-emerald-600 dark:text-emerald-300 text-sm mb-4">
                      <span className="font-medium">Looking for:</span> {listing.trade.trade_preferences}
                    </p>
                  )}
                  
                  {/* Propose Trade Button */}
                  {user && listing.user_id !== user.id && (
                    <button
                      onClick={() => setShowTradeModal(true)}
                      className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
                      data-testid="propose-trade-btn"
                    >
                      <ArrowLeftRight className="w-5 h-5" />
                      Propose a Trade
                    </button>
                  )}
                  {!user && (
                    <Link
                      to="/auth"
                      className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
                    >
                      <ArrowLeftRight className="w-5 h-5" />
                      Sign in to Propose Trade
                    </Link>
                  )}
                </div>
              )}
              
              {/* Trade Proposals Section - For listing owner */}
              {isOwner && (listing.listing_type === 'trade' || listing.trade?.open_for_trade) && (
                <div className="mt-6 p-4 bg-card rounded-xl border border-border" data-testid="trade-proposals-section">
                  <button
                    onClick={() => setShowTradeProposals(!showTradeProposals)}
                    className="w-full flex items-center justify-between text-foreground font-semibold"
                    data-testid="toggle-trade-proposals"
                  >
                    <span className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                        <ArrowLeftRight className="w-4 h-4 text-white" />
                      </div>
                      Trade Proposals
                      {tradeProposals.filter(p => p.status === 'pending').length > 0 && (
                        <span className="px-2 py-0.5 text-xs font-bold bg-emerald-500 text-white rounded-full">
                          {tradeProposals.filter(p => p.status === 'pending').length} new
                        </span>
                      )}
                    </span>
                    <ChevronRight className={`w-5 h-5 transition-transform ${showTradeProposals ? 'rotate-90' : ''}`} />
                  </button>
                  
                  {showTradeProposals && (
                    <div className="mt-4 space-y-3">
                      {loadingTradeProposals ? (
                        <div className="flex justify-center py-6">
                          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                        </div>
                      ) : tradeProposals.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground">
                          <ArrowLeftRight className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">No trade proposals yet</p>
                          <p className="text-xs mt-1">When someone proposes a trade, it will appear here</p>
                        </div>
                      ) : (
                        <>
                          {tradeProposals.map(proposal => (
                            <div 
                              key={proposal.id}
                              className={`p-4 rounded-xl ${
                                proposal.status === 'pending' 
                                  ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                  : proposal.status === 'accepted'
                                    ? 'bg-green-500/10 border border-green-500/30'
                                    : 'bg-secondary border border-border'
                              }`}
                            >
                              {/* Proposer info */}
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold">
                                  {proposal.proposer_avatar ? (
                                    <img src={proposal.proposer_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                                  ) : (
                                    proposal.proposer_name?.[0] || 'U'
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-foreground">{proposal.proposer_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(proposal.created_at).toLocaleDateString()} · 
                                    {proposal.status === 'pending' && <span className="text-amber-500 ml-1">Pending</span>}
                                    {proposal.status === 'accepted' && <span className="text-green-500 ml-1">Accepted</span>}
                                    {proposal.status === 'rejected' && <span className="text-red-500 ml-1">Declined</span>}
                                  </p>
                                </div>
                              </div>
                              
                              {/* What they're offering */}
                              <div className="p-3 bg-background rounded-lg mb-3">
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">They offer:</p>
                                {proposal.offered_listing_title && (
                                  <Link 
                                    to={`/listing/${proposal.offered_listing_id}`}
                                    className="flex items-center gap-2 mb-1 hover:text-primary transition-colors"
                                  >
                                    <Package className="w-4 h-4 text-emerald-500" />
                                    <span className="font-medium text-sm">{proposal.offered_listing_title}</span>
                                    {proposal.offered_listing_price && (
                                      <span className="text-xs text-muted-foreground">(${proposal.offered_listing_price?.toLocaleString()})</span>
                                    )}
                                  </Link>
                                )}
                                {proposal.offered_items_description && (
                                  <p className="text-sm text-foreground">
                                    {proposal.offered_listing_title ? '+ ' : ''}{proposal.offered_items_description}
                                  </p>
                                )}
                                {proposal.offered_value && (
                                  <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mt-1">
                                    Est. value: ${proposal.offered_value?.toLocaleString()}
                                  </p>
                                )}
                              </div>
                              
                              {/* Message */}
                              {proposal.message && (
                                <p className="text-sm text-muted-foreground italic mb-3">"{proposal.message}"</p>
                              )}
                              
                              {/* Actions for pending */}
                              {proposal.status === 'pending' && (
                                <div className="flex items-center gap-2">
                                  <Link
                                    to={`/trade-proposals?id=${proposal.id}`}
                                    className="flex-1 py-2 px-3 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1"
                                  >
                                    View & Respond
                                  </Link>
                                </div>
                              )}
                            </div>
                          ))}
                          
                          <Link
                            to="/trade-proposals"
                            className="block text-center py-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            View all trade proposals →
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Dynamic Property Groups - Uses tabs on desktop, headers on mobile */}
              <ListingPropertyGroups 
                listing={listing} 
                categoryProperties={categoryProperties} 
              />

              {listing.features?.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Features</h3>
                  <div className="flex flex-wrap gap-2">
                    {listing.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded-full text-sm"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description - Now after all property details */}
              {listing.description && (
                <div className="mt-8 pt-8 border-t border-border prose max-w-none">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Description</h3>
                  <p className="text-muted-foreground whitespace-pre-line" data-testid="listing-description">
                    {listing.description}
                  </p>
                </div>
              )}
              </div>
            </div>
          </div>

          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block lg:col-span-1">
            {/* Seller Info Card */}
            <div className="bg-card rounded-2xl p-5 shadow-lg border border-border sticky top-24">
              {/* Title & Price - Prominent at top */}
              <div className="mb-4 pb-4 border-b border-border">
                <h2 className="font-bold text-lg text-foreground line-clamp-2 mb-3" data-testid="listing-title">{listing.title}</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Price with original price struck through if reduced */}
                  {listing.pricing?.original_price && listing.pricing.original_price > listing.pricing.amount ? (
                    <div className="flex flex-col">
                      <span className="text-sm text-muted-foreground line-through">
                        <PriceDisplay 
                          amount={listing.pricing.original_price}
                          fromCurrency={listing.pricing?.currency || 'USD'}
                          size="sm"
                        />
                      </span>
                      <PriceDisplay 
                        amount={listing.pricing?.amount || listing.price}
                        fromCurrency={listing.pricing?.currency || 'USD'}
                        size="lg"
                        className="text-green-600 dark:text-green-500"
                      />
                    </div>
                  ) : (
                    <PriceDisplay 
                      amount={listing.pricing?.amount || listing.price}
                      fromCurrency={listing.pricing?.currency || 'USD'}
                      size="lg"
                      unit={listing.pricing?.price_unit}
                    />
                  )}
                  {listing.listing_type === 'rent' && (
                    <span className="text-xs text-muted-foreground">
                      / {getRentalPeriodLabel(listing.pricing?.rental_period || (listing.pricing?.is_hourly ? 'hourly' : 'monthly'), language)}
                    </span>
                  )}
                  {listing.pricing?.bidding_allowed && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-lg border border-amber-500/30">
                      <Gavel className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium text-amber-500">
                        {listing.bid_count > 0 ? `${listing.bid_count} bid${listing.bid_count !== 1 ? 's' : ''}` : 'Open for Bids'}
                      </span>
                    </div>
                  )}
                </div>
                {listing.pricing?.bidding_allowed && listing.pricing?.minimum_bid > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Minimum bid: <PriceDisplay amount={listing.pricing.minimum_bid} fromCurrency={listing.pricing?.currency || 'USD'} size="sm" />
                  </p>
                )}
                
                {/* Compact Auction Timer in Sidebar */}
                {listing.listing_type === 'auction' && (listing.auction?.auction_end_time || listing.auction?.end_time) && (
                  <div className="mt-3">
                    <AuctionTimer endTime={listing.auction.auction_end_time || listing.auction.end_time} compact={true} />
                  </div>
                )}
              </div>

              {/* Action Buttons Row - Like, Alert, Share */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <FavoriteButton listingId={listing.id} size="sm" />
                  <button 
                    onClick={togglePriceAlert}
                    disabled={alertLoading}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                      hasAlert 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-secondary text-muted-foreground hover:text-amber-500'
                    }`}
                    data-testid="price-alert-btn"
                    title={hasAlert ? 'Remove price alert' : 'Get notified on price changes'}
                  >
                    {alertLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : hasAlert ? (
                      <Bell className="w-4 h-4" />
                    ) : (
                      <BellOff className="w-4 h-4" />
                    )}
                  </button>
                  <button 
                    onClick={() => setShowShareModal(true)}
                    className="w-9 h-9 bg-secondary rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary transition-colors" 
                    data-testid="share-btn"
                    title="Share listing"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-xs text-muted-foreground flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5" /> {listing.views || 0}
                  </span>
                  {(listing.favorites > 0 || listing.favorite_count > 0) && (
                    <span className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-amber-500" /> {listing.favorites || listing.favorite_count || 0}
                    </span>
                  )}
                </span>
              </div>

              {/* For Sale by Owner / Broker Badge for real estate */}
              {(listing.category === 'real-estate' || listing.category === 'houses') && (listing.listing_type === 'sale' || listing.listing_type === 'rent') && (
                <div className="mb-4">
                  {listing.is_verified_business ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold rounded-lg border border-emerald-500/30">
                      <BadgeCheck className="w-3.5 h-3.5" />
                      {language === 'nl' ? 'Geverifieerde Makelaar' : 'Verified Broker'}
                    </span>
                  ) : listing.user_account_type === 'business' ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-semibold rounded-lg border border-purple-500/30">
                      <Briefcase className="w-3.5 h-3.5" />
                      {language === 'nl' ? 'Makelaar' : 'Real Estate Agent'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-semibold rounded-lg border border-blue-500/30">
                      <User className="w-3.5 h-3.5" />
                      {language === 'nl' ? 'Te koop door eigenaar' : 'For sale by owner'}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3 mb-3 pb-3 border-b border-border">
                <div className="avatar w-10 h-10 text-sm">
                  {listing.user_avatar ? (
                    <img src={listing.user_avatar} alt={listing.user_name} />
                  ) : (
                    listing.user_name?.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Link 
                    to={`/seller/${listing.user_id}`}
                    className="font-semibold text-foreground hover:text-primary transition-colors flex items-center gap-1 text-sm"
                    data-testid="seller-name"
                  >
                    <span className="truncate">{listing.user_name}</span>
                    {(listing.is_verified || listing.is_verified_business) && (
                      <BadgeCheck className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                    )}
                  </Link>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    {(listing.is_verified || listing.is_verified_business) ? (
                      <>
                        <Shield className="w-3 h-3 text-blue-500" />
                        <span className="text-blue-500 font-medium">Verified</span>
                      </>
                    ) : listing.phone_verified ? (
                      <>
                        <Phone className="w-3 h-3 text-green-500" />
                        <span className="text-green-500 font-medium">Phone Verified</span>
                      </>
                    ) : (
                      <span>Seller</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* View All Seller Listings */}
              <Link
                to={`/seller/${listing.user_id}`}
                className="w-full py-1.5 mb-2 text-center text-xs text-primary hover:bg-primary/10 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                data-testid="view-seller-listings"
              >
                <Package className="w-3.5 h-3.5" />
                View all listings from this seller
              </Link>

              {/* Seller Trust & Verification */}
              <div className="mb-4 p-3 bg-secondary/30 rounded-xl space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Trust Score Badge */}
                  {listing.user_id && (
                    <TrustScoreBadge userId={listing.user_id} size="small" />
                  )}
                  {/* Seller Since Badge */}
                  <SellerPerformance userId={listing.user_id} compact={true} />
                </div>
                {/* Additional Verification Badges */}
                {(listing.user_whatsapp || listing.user_usdt_erc20_address) && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-border/50">
                    {/* WhatsApp Verified Badge */}
                    {listing.user_whatsapp && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 text-green-600 dark:text-green-400 text-xs rounded-full border border-green-500/20">
                        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        WhatsApp
                      </span>
                    )}
                    {/* USDT/Crypto Badge */}
                    {listing.user_usdt_erc20_address && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs rounded-full border border-emerald-500/20">
                        <svg className="w-3 h-3" viewBox="0 0 32 32" fill="currentColor">
                          <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0zm7.994 18.394c-.093.387-.307.68-.64.88-.333.2-.74.3-1.22.3H17.6v4.36h-3.2v-4.36H9.866c-.48 0-.887-.1-1.22-.3-.333-.2-.547-.493-.64-.88V17.1h13.988v1.294zm0-3.2H8.006v-1.28c.093-.4.307-.7.64-.9.333-.2.74-.3 1.22-.3H14.4V8.34h3.2v4.374h4.534c.48 0 .887.1 1.22.3.333.2.547.5.64.9v1.28z"/>
                        </svg>
                        Accepts USDT
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Write Review Button - REMOVED: Reviews only allowed after purchase */}
              {/* Reviews are now triggered via "Mark as Sold" workflow */}

              {isOwner ? (
                <div className="space-y-3">
                  {/* Status-based controls */}
                  {listing.status === 'sold' ? (
                    // SOLD state
                    <>
                      <div className="w-full py-3 bg-red-500/10 text-red-600 rounded-xl font-semibold flex items-center justify-center gap-2 border border-red-500/20">
                        <Check className="w-5 h-5" />
                        {language === 'nl' ? 'Verkocht' : 'Marked as Sold'}
                      </div>
                      <button
                        onClick={handleUnmarkSold}
                        className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-xl transition-colors"
                        data-testid="unmark-sold-btn"
                      >
                        {language === 'nl' ? 'Heractiveren' : 'Reactivate Listing'}
                      </button>
                    </>
                  ) : listing.status === 'reserved' ? (
                    // RESERVED state
                    <>
                      <div className="w-full py-3 bg-orange-500/10 text-orange-600 rounded-xl font-semibold flex items-center justify-center gap-2 border border-orange-500/20">
                        <Clock className="w-5 h-5" />
                        {language === 'nl' ? 'Gereserveerd' : 'Marked as Reserved'}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleOpenMarkSoldModal}
                          className="flex-1 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-1 transition-colors"
                          data-testid="mark-sold-btn"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          {language === 'nl' ? 'Verkocht' : 'Mark Sold'}
                        </button>
                        <button
                          onClick={handleUnmarkReserved}
                          className="flex-1 py-2.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-xl transition-colors"
                          data-testid="unmark-reserved-btn"
                        >
                          {language === 'nl' ? 'Verwijderen' : 'Remove Reservation'}
                        </button>
                      </div>
                    </>
                  ) : listing.status === 'under_offer' ? (
                    // UNDER OFFER state
                    <>
                      <div className="w-full py-3 bg-amber-500/10 text-amber-600 rounded-xl font-semibold flex items-center justify-center gap-2 border border-amber-500/20">
                        <FileCheck className="w-5 h-5" />
                        {language === 'nl' ? 'Onder Bod' : 'Under Offer'}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleOpenMarkSoldModal}
                          className="flex-1 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-1 transition-colors"
                          data-testid="mark-sold-btn"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          {language === 'nl' ? 'Verkocht' : 'Mark Sold'}
                        </button>
                        <button
                          onClick={handleUnmarkUnderOffer}
                          disabled={markingUnderOffer}
                          className="flex-1 py-2.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-xl transition-colors disabled:opacity-50"
                          data-testid="unmark-under-offer-btn"
                        >
                          {markingUnderOffer ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (language === 'nl' ? 'Bod Verwijderen' : 'Remove Offer')}
                        </button>
                      </div>
                      {/* Price reduction still available */}
                      <button
                        onClick={() => setShowPriceReductionModal(true)}
                        className="w-full py-2.5 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-medium text-sm flex items-center justify-center gap-1.5 transition-colors"
                        data-testid="reduce-price-btn"
                      >
                        <TrendingDown className="w-4 h-4 text-emerald-500" />
                        {language === 'nl' ? 'Prijs Verlagen' : 'Reduce Price'}
                      </button>
                    </>
                  ) : (
                    // ACTIVE state - show all options with refined, professional UI
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        {/* Sold - Primary action, prominent but refined */}
                        <button
                          onClick={handleOpenMarkSoldModal}
                          className="py-2.5 bg-zinc-900 dark:bg-zinc-100 text-zinc-50 dark:text-zinc-900 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all shadow-sm hover:opacity-90"
                          data-testid="mark-sold-btn"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          {language === 'nl' ? 'Verkocht' : 'Sold'}
                        </button>
                        {/* Reserved - Secondary status, clean and muted */}
                        <button
                          onClick={handleMarkAsReserved}
                          className="py-2.5 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all"
                          data-testid="mark-reserved-btn"
                        >
                          <Clock className="w-4 h-4 text-orange-500" />
                          {language === 'nl' ? 'Gereserveerd' : 'Reserved'}
                        </button>
                        {/* Under Offer - Secondary status with amber accent */}
                        <button
                          onClick={handleMarkUnderOffer}
                          disabled={markingUnderOffer}
                          className="py-2.5 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                          data-testid="mark-under-offer-btn"
                        >
                          {markingUnderOffer ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4 text-amber-500" />}
                          {language === 'nl' ? 'Onder Bod' : 'Under Offer'}
                        </button>
                        {/* Reduce Price - Functional action, always calm and neutral */}
                        <button
                          onClick={() => setShowPriceReductionModal(true)}
                          className="py-2.5 bg-card text-card-foreground border border-border hover:bg-muted/50 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all"
                          data-testid="reduce-price-btn"
                        >
                          <TrendingDown className="w-4 h-4 text-emerald-500" />
                          {language === 'nl' ? 'Prijs Verlagen' : 'Reduce Price'}
                        </button>
                      </div>
                    </>
                  )}
                  <Link
                    to={`/edit-listing/${listing.id}`}
                    className="block w-full py-3 btn-outline text-center"
                    data-testid="edit-listing-btn"
                  >
                    {language === 'nl' ? 'Bewerken' : 'Edit Listing'}
                  </Link>
                </div>
              ) : (
                /* Contact Buttons - Show CTA if available, otherwise standard buttons */
                listing.cta_url && listing.is_verified_business ? (
                  <div className="space-y-2">
                    {/* Primary CTA Button - Full width, prominent */}
                    <a
                      href={normalizeUrl(listing.cta_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-primary/25 transition-all"
                      data-testid="desktop-cta-button"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {listing.cta_type === 'event' ? t('cta_view_event') : 
                       listing.cta_type === 'sale' ? t('cta_view_sale') : 
                       t('cta_view_promo')}
                    </a>
                    {/* Campaign Date Indicators */}
                    {(listing.campaign_start || listing.campaign_end) && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl border border-primary/20">
                        <div className="flex flex-col gap-2">
                          {/* Start date - show if in future */}
                          {listing.campaign_start && new Date(listing.campaign_start) > new Date() && (
                            <div className="flex items-center gap-2 text-sm">
                              <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center">
                                <Calendar className="w-3.5 h-3.5 text-amber-600" />
                              </div>
                              <div>
                                <span className="text-muted-foreground text-xs">{t('campaign_starts')}</span>
                                <p className="font-medium text-foreground text-xs">
                                  {new Date(listing.campaign_start).toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'es' ? 'es-ES' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          )}
                          {/* Currently running indicator */}
                          {listing.campaign_start && new Date(listing.campaign_start) <= new Date() && listing.campaign_end && new Date(listing.campaign_end) > new Date() && (
                            <div className="flex items-center gap-2 text-sm">
                              <div className="w-7 h-7 rounded-lg bg-green-500/20 flex items-center justify-center">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                              </div>
                              <span className="font-medium text-green-600 text-xs">{t('campaign_running')}</span>
                            </div>
                          )}
                          {/* End date */}
                          {listing.campaign_end && (
                            <div className="flex items-center gap-2 text-sm">
                              <div className="w-7 h-7 rounded-lg bg-red-500/20 flex items-center justify-center">
                                <Clock className="w-3.5 h-3.5 text-red-500" />
                              </div>
                              <div>
                                <span className="text-muted-foreground text-xs">{t('campaign_ends')}</span>
                                <p className="font-medium text-foreground text-xs">
                                  {new Date(listing.campaign_end).toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'es' ? 'es-ES' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    {listing.user_whatsapp ? (
                      <>
                        <a
                          href={`https://wa.me/${listing.user_whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi, I'm interested in "${listing.title}". Is it still available?`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center justify-center gap-1.5 transition-colors text-xs"
                          data-testid="whatsapp-seller-btn"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                          WhatsApp
                        </a>
                        <button
                          onClick={handleOpenChat}
                          className="flex-1 py-2 btn-primary flex items-center justify-center gap-1.5 text-xs rounded-lg"
                          data-testid="contact-seller-btn"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          Message
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={handleOpenChat}
                          className="py-2 px-4 btn-primary flex items-center justify-center gap-1.5 text-xs rounded-lg"
                          data-testid="contact-seller-btn"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          Message
                        </button>
                        {listing.contact_phone && (
                          <a
                            href={`tel:${listing.contact_phone}`}
                            className="py-2 px-4 btn-outline flex items-center justify-center gap-1.5 text-xs rounded-lg"
                            data-testid="call-seller-btn"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            Call
                          </a>
                        )}
                      </>
                    )}
                  </div>
                )
              )}


              {/* Contact Info - Desktop */}
              {(listing.contact_email || listing.contact_phone || listing.user_whatsapp) && (
                <div className="mt-5 pt-4 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Contact</p>
                  <div className="flex flex-wrap gap-2">
                    {listing.user_whatsapp && (
                      <a 
                        href={`https://wa.me/${listing.user_whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-green-500/10 rounded-lg hover:bg-green-500/20 transition-colors"
                        data-testid="whatsapp-contact-btn"
                      >
                        <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <span className="text-sm text-foreground">WhatsApp</span>
                      </a>
                    )}
                    {listing.contact_phone && (
                      <a 
                        href={`tel:${listing.contact_phone}`}
                        className="flex items-center gap-2 px-3 py-2 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                        data-testid="call-contact-btn"
                      >
                        <Phone className="w-4 h-4 text-primary" />
                        <span className="text-sm text-foreground">{listing.contact_phone}</span>
                      </a>
                    )}
                    {listing.contact_email && (
                      <a 
                        href={`mailto:${listing.contact_email}`}
                        className="flex items-center gap-2 px-3 py-2 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                        data-testid="email-contact-btn"
                      >
                        <Mail className="w-4 h-4 text-primary" />
                        <span className="text-sm text-foreground truncate max-w-[160px]">{listing.contact_email}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Location Map - Compact with Modal & Directions */}
              <div className="mt-4">
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-1.5 text-sm">
                  <MapPin className="w-4 h-4 text-primary" />
                  {t('location')}
                </h4>
                <ListingMap location={listing.location} title={listing.title} compact={true} t={t} />
              </div>

              {/* Report Listing */}
              {user && user.id !== listing.user_id && (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="mt-4 flex items-center gap-2 text-sm font-bold text-red-500 hover:text-red-600 transition-colors"
                  data-testid="report-listing-btn"
                >
                  <Flag className="w-4 h-4" />
                  Report this listing
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Category-targeted Ad Banner */}
        <div className="mt-8">
          <LargePromoSection position="listing_detail" category={listing.category} />
        </div>
        
        {/* Safety Tips - Full width at bottom */}
        <div className="mt-8 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h4 className="font-semibold text-amber-700 dark:text-amber-300">{t('safety_tips')}</h4>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <span className="text-amber-500 mt-0.5">•</span>
              <span>{t('meet_public')}</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <span className="text-amber-500 mt-0.5">•</span>
              <span>{t('check_item')}</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <span className="text-amber-500 mt-0.5">•</span>
              <span>{t('no_advance')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Modal */}
      {showChat && (
        <div className="fixed inset-0 z-[150] flex items-end md:items-center justify-center pb-20 md:pb-0" data-testid="chat-modal">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowChat(false)}
          />
          
          {/* Chat Panel - Mobile: taller bottom sheet, Desktop: wider centered modal */}
          <div className="relative w-full max-w-2xl mx-4 bg-card rounded-t-3xl md:rounded-2xl shadow-2xl flex flex-col md:max-h-[85vh] max-h-[80vh] min-h-[450px]">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
              <div className="flex items-center gap-3">
                <div className="avatar w-10 h-10 text-sm">
                  {listing.user_avatar ? (
                    <img src={listing.user_avatar} alt={listing.user_name} />
                  ) : (
                    listing.user_name?.charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{listing.user_name}</h3>
                  <p className="text-xs text-muted-foreground truncate max-w-[280px] md:max-w-[400px]">{listing.title}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowChat(false)}
                className="w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center transition-colors"
                data-testid="close-chat-btn"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 min-h-[250px]">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground text-sm">Start the conversation!</p>
                  <p className="text-muted-foreground/60 text-xs mt-1">
                    Send a message to {listing.user_name}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {messages.map((msg) => (
                    <div 
                      key={msg.id}
                      className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                    <div 
                      className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                        msg.sender_id === user?.id 
                          ? 'bg-primary text-white rounded-br-md' 
                          : 'bg-secondary text-foreground rounded-bl-md'
                      }`}
                    >
                      {msg.image_url && (
                        <div className="relative group mb-2">
                          <img 
                            src={msg.image_url} 
                            alt="Attached" 
                            className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setChatImageLightbox(msg.image_url)}
                          />
                          <div 
                            className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg cursor-pointer"
                            onClick={() => setChatImageLightbox(msg.image_url)}
                          >
                            <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      )}
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${
                        msg.sender_id === user?.id ? 'text-white/60' : 'text-muted-foreground'
                      }`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  ))}
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-border shrink-0">
              {/* Photo Preview */}
              {chatPhotoPreview && (
                <div className="mb-3 relative inline-block">
                  <img 
                    src={chatPhotoPreview} 
                    alt="Preview" 
                    className="h-20 w-auto rounded-lg border border-border"
                  />
                  <button
                    onClick={removeChatPhoto}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              <div className="flex gap-2 items-end">
                {/* Photo Upload Button */}
                <input
                  type="file"
                  ref={chatPhotoInputRef}
                  onChange={handleChatPhotoSelect}
                  accept="image/*,.heic,.heif"
                  className="hidden"
                />
                <button
                  onClick={() => chatPhotoInputRef.current?.click()}
                  className="w-10 h-10 rounded-full bg-secondary hover:bg-secondary/80 flex items-center justify-center transition-colors touch-manipulation active:scale-95 shrink-0"
                  title="Attach photo"
                  data-testid="attach-photo-btn"
                >
                  <ImagePlus className="w-5 h-5 text-muted-foreground" />
                </button>
                
                <textarea
                  ref={chatTextareaRef}
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    // Auto-resize textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type your message..."
                  className="flex-1 form-input py-3 px-4 resize-none overflow-y-auto min-h-[48px] max-h-[150px] text-sm leading-relaxed"
                  rows={2}
                  data-testid="chat-input"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={sending || (!newMessage.trim() && !chatPhoto)}
                  className="w-11 h-11 bg-primary hover:bg-primary/90 text-white rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shrink-0 transition-colors"
                  data-testid="send-chat-btn"
                >
                  {sending || uploadingPhoto ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Image Lightbox */}
      {chatImageLightbox && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={() => setChatImageLightbox(null)}
          data-testid="chat-image-lightbox"
        >
          <button 
            onClick={() => setChatImageLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img 
            src={chatImageLightbox} 
            alt="Full size" 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Full-Screen Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 backdrop-blur-sm touch-none"
          onClick={() => setShowImageModal(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={(e) => { e.preventDefault(); handleTouchMove(e); }}
          onTouchEnd={handleTouchEnd}
          data-testid="image-modal"
        >
          {/* Close button */}
          <button 
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-20"
          >
            <X className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </button>
          
          {/* Image counter */}
          {images.length > 1 && (
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/60 rounded-full text-white text-sm z-20">
              {selectedImage + 1} / {images.length}
            </div>
          )}
          
          {/* Main image container - takes full space minus thumbnails on mobile */}
          <div 
            className="flex items-center justify-center w-full h-full p-4 sm:p-8"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingTop: '60px', paddingBottom: images.length > 1 ? '80px' : '60px' }}
          >
            <img 
              src={images[selectedImage]} 
              alt={listing.title} 
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
            />
          </div>
          
          {/* Navigation arrows - smaller on mobile */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1)); }}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-20"
              >
                <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1)); }}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-20"
              >
                <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </button>
            </>
          )}
          
          {/* Thumbnail strip - hidden on mobile, smaller on tablet */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 hidden sm:flex gap-1.5 sm:gap-2 p-1.5 sm:p-2 bg-black/60 rounded-xl z-20 max-w-[90vw] overflow-x-auto">
              {images.slice(0, 8).map((img, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setSelectedImage(index); }}
                  className={`w-12 h-9 sm:w-16 sm:h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                    selectedImage === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {images.length > 8 && (
                <div className="w-12 h-9 sm:w-16 sm:h-12 rounded-lg bg-white/20 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                  +{images.length - 8}
                </div>
              )}
            </div>
          )}
          
          {/* Dot indicator for mobile (when thumbnails hidden) */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 sm:hidden flex gap-1.5 z-20">
              {images.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    selectedImage === index ? 'bg-white w-4' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mark as Sold Modal */}
      {showMarkSoldModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowMarkSoldModal(false)}
          />
          <div className="relative bg-card rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-green-500" />
                Mark as Sold
              </h3>
              <button
                onClick={() => setShowMarkSoldModal(false)}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-5">
              {/* Sale Price */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Sale Price (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <input
                    type="number"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-3 bg-secondary/50 border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    data-testid="sale-price-input"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={saleNotes}
                  onChange={(e) => setSaleNotes(e.target.value)}
                  placeholder="Any additional notes about the sale..."
                  rows={2}
                  className="w-full p-3 bg-secondary/50 border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  data-testid="sale-notes-input"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowMarkSoldModal(false)}
                  className="flex-1 py-3 btn-outline"
                  disabled={markingSold}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkAsSold}
                  disabled={markingSold}
                  className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  data-testid="confirm-mark-sold-btn"
                >
                  {markingSold ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Confirm Sale
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      
      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
        listing={listing}
      />

      {/* Report Listing Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowReportModal(false);
              setReportReason('');
            }}
          />
          <div className="relative bg-card rounded-2xl w-full max-w-md">
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Flag className="w-5 h-5 text-red-500" />
                Report Listing
              </h3>
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportReason('');
                }}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
                data-testid="close-report-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Help us keep the marketplace safe. Please describe why you're reporting this listing.
              </p>

              {/* Reason Options */}
              <div className="space-y-3">
                {[
                  'Fraudulent or scam listing',
                  'Prohibited item',
                  'Misleading description',
                  'Stolen goods',
                  'Inappropriate content',
                  'Other'
                ].map((option) => (
                  <button
                    key={option}
                    onClick={() => setReportReason(option)}
                    className={`w-full p-3 text-left text-sm rounded-xl border transition-colors ${
                      reportReason === option || (option === 'Other' && reportReason && !['Fraudulent or scam listing', 'Prohibited item', 'Misleading description', 'Stolen goods', 'Inappropriate content'].includes(reportReason))
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50 text-foreground'
                    }`}
                    data-testid={`report-option-${option.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              {/* Custom Reason Input - only show when "Other" is selected */}
              {(reportReason === 'Other' || (reportReason && !['Fraudulent or scam listing', 'Prohibited item', 'Misleading description', 'Stolen goods', 'Inappropriate content'].includes(reportReason))) && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Please describe the issue
                  </label>
                  <textarea
                    value={reportReason === 'Other' ? '' : reportReason}
                    onChange={(e) => setReportReason(e.target.value || 'Other')}
                    placeholder="Provide details about your report..."
                    rows={3}
                    className="w-full px-4 py-3 bg-secondary/50 border border-border rounded-xl text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                    data-testid="report-reason-input"
                    autoFocus
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason('');
                  }}
                  className="flex-1 px-4 py-3 border border-border rounded-xl text-foreground hover:bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!reportReason.trim()) {
                      toast.error('Please select or describe a reason for reporting');
                      return;
                    }
                    setReportingListing(true);
                    try {
                      await axios.post(`${API}/listings/${listing.id}/report`, 
                        { listing_id: listing.id, reason: reportReason.trim() });
                      toast.success('Listing reported. Our team will review it.');
                      setShowReportModal(false);
                      setReportReason('');
                    } catch (error) {
                      if (error.response?.status === 400) {
                        toast.error('You have already reported this listing');
                      } else {
                        toast.error('Failed to report listing');
                      }
                    } finally {
                      setReportingListing(false);
                    }
                  }}
                  disabled={!reportReason.trim() || reportingListing}
                  className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-report-btn"
                >
                  {reportingListing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Location BottomSheet */}
      <BottomSheet
        isOpen={showMobileLocationModal}
        onClose={() => setShowMobileLocationModal(false)}
        title={t('location')}
        testId="mobile-location-sheet"
        maxHeight="85vh"
      >
        <div className="p-4">
          <ListingMap location={listing?.location} title={listing?.title} compact={false} t={t} />
        </div>
      </BottomSheet>

      {/* Trade Proposal Modal */}
      {showTradeModal && listing && (
        <ProposeTradeModal
          listing={listing}
          onClose={() => setShowTradeModal(false)}
          onSuccess={() => {
            toast.success('Your trade proposal has been sent!');
          }}
        />
      )}

      {/* Boost Listing Modal - shown after publishing a new listing */}
      <BoostListingModal
        isOpen={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        listingId={listing?.id}
        listingTitle={listing?.title}
      />

      {/* Yard Sale Item Photo Modal */}
      {yardSalePhotoModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={() => setYardSalePhotoModal(null)}
          data-testid="yard-sale-photo-modal"
        >
          <button
            onClick={() => setYardSalePhotoModal(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
            data-testid="close-photo-modal"
          >
            <X className="w-6 h-6" />
          </button>
          <div 
            className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={yardSalePhotoModal.image}
              alt={yardSalePhotoModal.name}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 text-white text-center">
              <h3 className="text-lg font-semibold">{yardSalePhotoModal.name}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Price Reduction Modal */}
      <PriceReductionModal
        isOpen={showPriceReductionModal}
        onClose={() => setShowPriceReductionModal(false)}
        onConfirm={handleConfirmPriceReduction}
        currentPrice={listing?.pricing?.amount || 0}
        originalPrice={listing?.pricing?.original_price}
        currency={listing?.pricing?.currency || 'USD'}
        isLoading={reducingPrice}
      />
    </div>
  );
};

export default ListingDetailPage;
